// Brissta purchase UI
// Custom purchase type cards for subscription and one-time purchase flows

(function () {
  'use strict';

  const DUO_SUBSCRIBE_ONE_DISCOUNT = 0.2;
  const DUO_SUBSCRIBE_TWO_DISCOUNT = 0.253;
  let currentPurchaseState = 'subscribe-one';
  let addToCartInFlight = false;
  let lastRoutePathname = window.location.pathname;
  let hardReloadInFlight = false;

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  function parsePrice(value) {
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    return parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0;
  }

  function money(value) {
    const amount = Number(value || 0);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function roundMoney(value) {
    return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
  }

  // -----------------------------------------------------------------------
  // 3-OFFER MODEL — for cafe-au-lait-bag, americano-sticks.
  // Other PDPs keep the legacy 2-card (qty 1 / qty 2) offer model below.
  //
  // Offer layout in the design:
  //   Offer 0: 3 of variant 1   (e.g. "3 Travel Packs", "3 Mini Eco-Bags")
  //   Offer 1: 1 of variant 2   (e.g. "1 Carton",        "1 Eco-Bag")
  //   Offer 2: 2 of variant 2   (e.g. "2 Cartons",       "2 Eco-Bags")
  // -----------------------------------------------------------------------
  const TARGET_PDP_HANDLES = [
    'cafe-au-lait', 'cafe-au-lait-bag',
    'americano', 'americano-sticks'
  ];
  const OFFER_CONFIG = [
    { variantIndex: 0, quantity: 1 },
    { variantIndex: 1, quantity: 1 },
    { variantIndex: 1, quantity: 2 }
  ];

  // Some PDPs hide specific offers because the backing Shopify variant was
  // removed. americano-sticks no longer sells the "3 Travel Packs" small unit
  // (offer index 0), so it renders only the Carton offers (indices 1 and 2).
  function getHiddenOfferIndices() {
    const path = (window.location.pathname || '').toLowerCase();
    if (path.includes('americano-sticks')) return [0];
    return [];
  }
  function isOfferVisible(idx) {
    return getHiddenOfferIndices().indexOf(idx) === -1;
  }
  function getVisibleOfferIndices() {
    return OFFER_CONFIG.map(function (_, idx) { return idx; }).filter(isOfferVisible);
  }
  function defaultOfferIndex() {
    const visible = getVisibleOfferIndices();
    return visible.length ? visible[0] : 0;
  }

  function isTargetPDP() {
    const m = (window.location.pathname || '').toLowerCase().match(/^\/products\/([^\/\?]+)/);
    return !!m && TARGET_PDP_HANDLES.includes(m[1]);
  }

  // americano-mini-pack has a single "3 Packs" variant, so it uses the
  // legacy 2-card model (qty 1 vs qty 2 of that variant). But it should hide
  // its native Serving Size selector like the target PDPs and label its two
  // offers "3 Packs" (1 unit / 9 servings) and "6 Packs" (2 units / 18 servings).
  function isMiniPackPDP() {
    return (window.location.pathname || '').toLowerCase().includes('/products/americano-mini-pack');
  }

  function getServingTypeRadios() {
    const all = document.querySelectorAll('.xo-product-variant__inner-value--boxed input.xo-field-radio-3__input');
    return Array.from(all).filter((input) => !input.closest('.brissta-purchase-type-variants'));
  }

  // Parses the embedded variant-prices JSON injected by main-product.liquid.
  // Returns an array ordered the same as Shopify's product.variants.
  // Falls back to empty array if not present (e.g., on non-target PDPs).
  function getProductVariantData() {
    const node = document.querySelector('script[data-brissta-variant-prices]');
    if (!node) return [];
    try { return JSON.parse(node.textContent || '[]'); }
    catch (_) { return []; }
  }

  // Find the product variant that matches the radio at index `i` so we can
  // pull its price from getProductVariantData(). Falls back to index lookup
  // if the radio doesn't expose xo-variant-id.
  function getVariantInfoForRadioIndex(i) {
    const variants = getProductVariantData();
    const radios = getServingTypeRadios();
    const radio = radios[i];
    if (radio) {
      const vid = String(radio.getAttribute('xo-variant-id') || '');
      const match = variants.find((v) => String(v.id) === vid);
      if (match) return match;
    }
    return variants[i] || null;
  }

  // Resolve which variant + radio backs a given OFFER_CONFIG.variantIndex by
  // matching variant TITLE (admin variant order isn't guaranteed to be
  // small-then-large). variantIndex 0 → small unit (mini / travel / pack);
  // variantIndex 1 → large unit (carton / eco-bag, excluding any "mini").
  function isSmallUnitTitle(title) {
    const t = String(title || '').toLowerCase();
    return /mini|travel|\bpack\b/.test(t);
  }
  function getVariantInfoForOfferLogicalIndex(logicalIdx) {
    const variants = getProductVariantData();
    if (!variants.length) return null;
    if (logicalIdx === 0) {
      return variants.find((v) => isSmallUnitTitle(v.title)) || variants[0];
    }
    return variants.find((v) => !isSmallUnitTitle(v.title)) || variants[1] || variants[0];
  }
  function getServingTypeRadioForOfferLogicalIndex(logicalIdx) {
    const radios = getServingTypeRadios();
    if (!radios.length) return null;
    const matchByTitle = function (test) {
      return radios.find(function (r) {
        const label = (r.getAttribute('xo-label') || r.value || '').toLowerCase();
        return test(label);
      });
    };
    if (logicalIdx === 0) {
      return matchByTitle(isSmallUnitTitle) || radios[0];
    }
    return matchByTitle(function (l) { return !isSmallUnitTitle(l); }) || radios[1] || radios[0];
  }

  function selectServingTypeVariant(logicalIdx) {
    // logicalIdx is the OFFER_CONFIG.variantIndex (0 = small, 1 = large).
    // Resolve to the actual radio by TITLE rather than DOM position so we
    // pick the right variant even if Shopify's admin order differs.
    const target = getServingTypeRadioForOfferLogicalIndex(logicalIdx);
    if (!target || target.checked) return;
    target.click();
  }

  // Moves #brissta-purchase-type-label and .brissta-purchase-type-variants
  // out of the widget and inserts them ABOVE the serving-type's boxed radios.
  // Defensive: only cleans up old "outside" copies AFTER confirming the widget
  // contains fresh ones to insert (otherwise we'd wipe what's currently shown
  // and leave nothing visible if the widget innerHTML happened to be empty).
  function relocatePurchaseTypeElements(widget) {
    if (!widget) return;

    const purchaseLabel = widget.querySelector('#brissta-purchase-type-label');
    const purchaseVariants = widget.querySelector('.brissta-purchase-type-variants');
    if (!purchaseLabel && !purchaseVariants) return; // nothing fresh — keep existing moved copies untouched

    const allBoxed = $$('.xo-product-variant__inner-value--boxed');
    const servingBoxed = Array.from(allBoxed).find((el) => !el.closest('.brissta-purchase-type-variants') && !widget.contains(el));
    // No stable insertion point — abandon the move; keep any existing
    // outside copies as-is and leave the new ones inside the widget.
    if (!servingBoxed || !servingBoxed.parentNode) return;

    // Now safe to clean up stragglers — we have new ones to put back.
    document.querySelectorAll('#brissta-purchase-type-label').forEach((el) => {
      if (!widget.contains(el)) el.remove();
    });
    document.querySelectorAll('.brissta-purchase-type-variants').forEach((el) => {
      if (!widget.contains(el)) el.remove();
    });

    if (purchaseLabel) servingBoxed.parentNode.insertBefore(purchaseLabel, servingBoxed);
    if (purchaseVariants) servingBoxed.parentNode.insertBefore(purchaseVariants, servingBoxed);
  }

  function offerIndexOf(state) {
    let idx;
    if (state === 'subscribe-two' || state === 'onetime-two') idx = 2;
    else if (state === 'subscribe-one' || state === 'onetime-one') idx = 1;
    else {
      const parts = String(state || '').split('-');
      const parsed = parseInt(parts[1], 10);
      idx = (!isNaN(parsed) && OFFER_CONFIG[parsed]) ? parsed : defaultOfferIndex();
    }
    // A saved/URL state that points at a hidden offer falls back to the
    // first visible one (e.g. offer 0 on americano-sticks → offer 1).
    if (!isOfferVisible(idx)) return defaultOfferIndex();
    return idx;
  }

  function variantIndexOfState(state) {
    const offer = OFFER_CONFIG[offerIndexOf(state)];
    return offer ? offer.variantIndex : 0;
  }

  // Reads ?offer=subscribe-0 (or onetime-2, etc) from the URL and returns
  // a valid state string if present + supported on this PDP. The shop
  // collection's product-card variant links use this to deep-link a specific
  // offer card pre-selected.
  function getOfferStateFromUrl() {
    if (!isTargetPDP()) return null;
    const params = new URLSearchParams(window.location.search || '');
    const raw = params.get('offer');
    if (!raw) return null;
    const m = /^(subscribe|onetime)-(\d+)$/.exec(raw);
    if (!m) return null;
    const idx = parseInt(m[2], 10);
    if (!OFFER_CONFIG[idx]) return null;
    if (!isOfferVisible(idx)) return null;
    return `${m[1]}-${idx}`;
  }

  function getSavedState() {
    if (!currentPurchaseState) {
      const fromUrl = getOfferStateFromUrl();
      if (fromUrl) currentPurchaseState = fromUrl;
    }
    return currentPurchaseState || (isTargetPDP() ? `subscribe-${defaultOfferIndex()}` : 'subscribe-one');
  }

  function saveState(value) {
    currentPurchaseState = value || 'subscribe-one';
  }

  function defaultQuantityFor(purchaseType) {
    if (isTargetPDP()) return defaultOfferIndex(); // default offer index in 3-offer model
    return purchaseType === 'onetime' ? 2 : 1;
  }

  function purchaseTypeOf(state) {
    return String(state || '').startsWith('onetime') ? 'onetime' : 'subscribe';
  }

  function quantityOf(state) {
    // 3-offer model state format: "${type}-${offerIdx}" (e.g. subscribe-0)
    const offer = OFFER_CONFIG[offerIndexOf(state)];
    if (isTargetPDP() && offer) return offer.quantity;
    // Legacy 2-card model
    if (state === 'subscribe-two' || state === 'onetime-two') return 2;
    return 1;
  }

  function buildState(purchaseType, quantityOrOfferIdx) {
    if (isTargetPDP()) {
      const offerIdx = typeof quantityOrOfferIdx === 'number' ? quantityOrOfferIdx : defaultOfferIndex();
      return `${purchaseType}-${offerIdx}`;
    }
    const qty = quantityOrOfferIdx === 2 ? 'two' : 'one';
    return purchaseType === 'onetime' ? `onetime-${qty}` : `subscribe-${qty}`;
  }

  function isProductPath(pathname) {
    return /^\/products\/[^/]+/.test(String(pathname || ''));
  }

  function handleRouteChange() {
    const nextPathname = window.location.pathname;
    const pathnameChanged = nextPathname !== lastRoutePathname;
    const productToProductNavigation = pathnameChanged && isProductPath(lastRoutePathname) && isProductPath(nextPathname);

    lastRoutePathname = nextPathname;

    if (productToProductNavigation && !hardReloadInFlight) {
      hardReloadInFlight = true;
      window.location.reload();
    }
  }

  function bindRouteChangeReload() {
    if (window._brisstaRouteReloadBound) return;
    window._brisstaRouteReloadBound = true;

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function () {
      const result = originalPushState.apply(this, arguments);
      handleRouteChange();
      return result;
    };

    window.history.replaceState = function () {
      const result = originalReplaceState.apply(this, arguments);
      handleRouteChange();
      return result;
    };

    window.addEventListener('popstate', handleRouteChange);
  }

  function getSelectedVariantInput() {
    // After the Purchase Type radios are relocated above the Serving Type
    // radios, the first checked .xo-field-radio-3__input in document order
    // is the purchase-type one — which has no xo-variant-id. Filter those out
    // so we always return the actual product-variant radio.
    const all = $$('input.xo-field-radio-3__input:checked');
    return all.find((input) => !input.closest('.brissta-purchase-type-variants')) || null;
  }

  function getSelectedVariantName() {
    const input = getSelectedVariantInput();
    return input ? (input.getAttribute('xo-label') || input.value || '').trim() : '';
  }

  function getSelectedVariantId() {
    const input = getSelectedVariantInput();
    return input ? String(input.getAttribute('xo-variant-id') || '') : '';
  }

  function isDuoProduct() {
    return getCurrentProductKey().includes('duo');
  }

  function getUnitLabel() {
    const name = getSelectedVariantName().toLowerCase();
    if (name.includes('stick')) return 'sticks';
    if (name.includes('bag')) return 'bag';
    return 'units';
  }

  function getMainProductRoot() {
    return $('.main-product .xo-product-info-content__buy-button') ||
      $('.main-product') ||
      $('.xo-product-info-content__buy-button') ||
      document;
  }

  function getAllAddToCartForms() {
    const form = getProductForm();
    return form ? [form] : [];
  }

  function getProductForm() {
    const root = getMainProductRoot();
    return $('form[action*="/cart/add"][data-type="add-to-cart-form"]', root) ||
      $('form[action*="/cart/add"]', root) ||
      null;
  }

  function getProductRoot() {
    return $('.main-product xo-product.main-product__wrapper') ||
      $('.main-product xo-product') ||
      $('xo-product.main-product__wrapper') ||
      $('xo-product');
  }

  function getProductId() {
    return String(getProductRoot()?.getAttribute('xo-product-id') || '');
  }

  function syncFormVariantId() {
    const form = getProductForm();
    const variantId = getSelectedVariantId();
    if (!form || !variantId) return;
    const input = $('input[name="id"]', form);
    if (input) {
      input.value = variantId;
    }
  }

  function syncFormPurchaseState() {
    const state = normalizeState(getSavedState());
    const block = getBlockForVariant({ allowFallback: true });

    setFormQuantity(quantityOf(state));
    if (block) setPurchaseMode(block, purchaseTypeOf(state));
  }

  function getServiceSections() {
    const cartMiniId = document.querySelector('xo-cart-mini')?.id || '';
    if (window.Xotiny) {
      return window.location.pathname.includes('/cart') ? [cartMiniId].filter(Boolean) : [cartMiniId].filter(Boolean);
    }
    return window.location.pathname.includes('/cart')
      ? ['cart-icon-bubble', 'cart-live-region-text']
      : ['cart-drawer', 'cart-icon-bubble'];
  }

  function updateCartSizeFromMini(cartMiniEl) {
    if (!cartMiniEl) return;
    const quantityInputs = $$('input[name="quantity"]', cartMiniEl);
    const count = quantityInputs.reduce((sum, input) => sum + (parseInt(input.value, 10) || 0), 0);
    $$('xo-cart-size').forEach((el) => {
      el.textContent = String(count);
    });
  }

  function applyCartSections(sections) {
    if (!sections || typeof sections !== 'object') return;

    const cartMiniEl = document.querySelector('xo-cart-mini');
    const cartMiniId = cartMiniEl?.id;
    if (cartMiniEl && cartMiniId && sections[cartMiniId]) {
      const doc = new DOMParser().parseFromString(sections[cartMiniId], 'text/html');
      const nextMini = doc.querySelector('xo-cart-mini');
      if (nextMini) {
        cartMiniEl.innerHTML = nextMini.innerHTML;
        updateCartSizeFromMini(cartMiniEl);
      }
    }
  }

  async function addCurrentProductToCart() {
    const form = getProductForm();
    if (!form || addToCartInFlight) return false;

    addToCartInFlight = true;

    try {
      syncFormPurchaseState();
      syncFormVariantId();

      const formData = new FormData(form);
      const productId = getProductId();
      if (productId && !formData.get('product-id')) {
        formData.append('product-id', productId);
      }
      formData.set('sections', getServiceSections().join(','));
      formData.set('section_url', window.location.pathname);

      const response = await fetch(`${window.Shopify.routes.root}cart/add`, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        let message = 'Add to cart failed';
        try {
          const data = await response.json();
          message = data?.description || data?.message || message;
        } catch (_) {}
        throw new Error(message);
      }

      const data = await response.json();
      if (data?.status) {
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      applyCartSections(data.sections);
      if (window.xoModal?.open) {
        window.xoModal.open('cart');
      }
      return true;
    } finally {
      addToCartInFlight = false;
    }
  }

  function ensureSellingPlanInputs() {
    return getAllAddToCartForms().map((form) => {
      let input = $('input[name="selling_plan"]', form);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        form.appendChild(input);
      }
      return input;
    });
  }

  function setFormQuantity(quantity) {
    const normalized = String(Math.max(1, Math.floor(Number(quantity) || 1)));
    getAllAddToCartForms().forEach((form) => {
      let input = $('input[name="quantity"]', form);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        form.appendChild(input);
      }
      input.value = normalized;
    });
  }

  function getBlockForVariant(options = {}) {
    const { allowFallback = false } = options;
    const blocks = $$('.shopify_subscriptions_app_block');
    const variantId = getSelectedVariantId();
    if (!variantId) {
      return allowFallback ? (blocks[0] || null) : null;
    }
    return blocks.find((block) => String(block.dataset.variantId || '') === variantId) || null;
  }

  function getSellingPlanInput(block) {
    if (!block) return null;
    return $('input[data-radio-type="selling_plan"]', block);
  }

  function getOneTimeInput(block) {
    if (!block) return null;
    return $('input[data-radio-type="one_time_purchase"]', block);
  }

  function getCurrentProductKey() {
    const title = ($('.xo-product-info-content__title')?.textContent || '').trim().toLowerCase();
    const path = window.location.pathname.toLowerCase();
    return `${title} ${path}`;
  }

  function getDiscountConfig() {
    if (isDuoProduct()) {
      return {
        subscribeOneDiscount: DUO_SUBSCRIBE_ONE_DISCOUNT,
        subscribeTwoDiscount: DUO_SUBSCRIBE_TWO_DISCOUNT,
        subscribeOneMultiplier: 0.8,
        subscribeTwoMultiplier: 0.747,
        subscribeOneBadge: '20% off',
        subscribeTwoBadge: '25% off',
        subscribeOneRange: [0.78, 0.82],
        subscribeTwoRange: [0.73, 0.76]
      };
    }

    return {
      subscribeOneDiscount: 0.30,
      subscribeTwoDiscount: 0.35,
      // Fallbacks if Shopify's selling-plan data isn't readable client-side.
      subscribeOneMultiplier: 0.70,
      subscribeTwoMultiplier: 0.65,
      subscribeOneBadge: '10% off',
      subscribeTwoBadge: '15% off',
      // Allowed ratio ranges when scanning the selling-plan input's dataset
      // for the actual discounted unit price. Wide enough to cover 25%–35%
      // off so the PDP price mirrors what xo-mini-cart .price--final shows.
      subscribeOneRange: [0.65, 0.75],
      subscribeTwoRange: [0.60, 0.70]
    };
  }

  function getPricing(block) {
    const planInput = getSellingPlanInput(block);
    const oneTimeInput = getOneTimeInput(block);
    const discountConfig = getDiscountConfig();
    const originalUnitPrice = parsePrice(
      oneTimeInput?.dataset.variantPrice ||
      planInput?.dataset.variantCompareAtPrice ||
      planInput?.dataset.variantPrice
    );

    const subscribeOneRatio = getDiscountMultiplier(
      planInput,
      originalUnitPrice,
      discountConfig.subscribeOneMultiplier,
      discountConfig.subscribeOneRange
    );
    const subscribeTwoRatio = getDiscountMultiplier(
      planInput,
      originalUnitPrice,
      discountConfig.subscribeTwoMultiplier,
      discountConfig.subscribeTwoRange
    );

    const subscribeOneUnitPrice = roundMoney(originalUnitPrice * subscribeOneRatio);
    const subscribeTwoUnitPrice = roundMoney(originalUnitPrice * subscribeTwoRatio);

    return {
      originalUnitPrice: roundMoney(originalUnitPrice),
      subscribeOneUnitPrice,
      subscribeTwoUnitPrice,
      subscribeOneTotalPrice: roundMoney(subscribeOneUnitPrice),
      subscribeTwoTotalPrice: roundMoney(subscribeTwoUnitPrice * 2),
      subscribeOneBadge: discountConfig.subscribeOneBadge,
      subscribeTwoBadge: discountConfig.subscribeTwoBadge
    };
  }

  function getDiscountMultiplier(planInput, originalUnitPrice, fallbackMultiplier, preferredRange) {
    if (!planInput || !originalUnitPrice) return fallbackMultiplier;

    const numericDatasets = Object.entries(planInput.dataset || {})
      .map(([, value]) => parsePrice(value))
      .filter((value) => value > 0);

    const ratios = numericDatasets
      .map((value) => value / originalUnitPrice)
      .filter((ratio) => ratio > 0.5 && ratio < 1);

    const rangedMatch = ratios.find((ratio) => ratio >= preferredRange[0] && ratio <= preferredRange[1]);
    return rangedMatch || fallbackMultiplier;
  }

  function setPurchaseMode(block, mode) {
    const sellingPlanInputs = ensureSellingPlanInputs();
    const planInput = getSellingPlanInput(block);
    const sellingPlanId = mode === 'subscribe' ? String(planInput?.dataset.sellingPlanId || '') : '';

    sellingPlanInputs.forEach((input) => {
      input.value = sellingPlanId;
    });
  }

  function getVisiblePrimarySubmit() {
    const form = getProductForm();
    if (!form) return null;
    return $('.xo-buy-button__cart-add-add-to-card button[type="submit"], .xo-buy-button__cart-add-add-to-card button', form);
  }

  function getManagedPrimarySubmit() {
    const button = getVisiblePrimarySubmit();
    if (!button) return null;
    if (button.dataset.brisstaManaged === 'true') return button;

    const clone = button.cloneNode(true);
    clone.dataset.brisstaManaged = 'true';
    clone.type = 'button';
    clone.removeAttribute('form');
    button.replaceWith(clone);
    return clone;
  }

  function getPrimaryButtonContainer() {
    const button = getVisiblePrimarySubmit();
    return button ? button.closest('.xo-buy-button__cart-add-add-to-card') : null;
  }

  function getSubscribeAddHost(primaryButtonContainer = getPrimaryButtonContainer()) {
    return primaryButtonContainer ? primaryButtonContainer.closest('.shopify-payment-button__add') : null;
  }

  function setPrimaryButtonVisibility(hidden) {
    const buyWrap = $('.xo-product-info-content__buy-button');
    if (!buyWrap) return;
    buyWrap.classList.toggle('brissta-primary-hidden', !!hidden);
  }

  function hasSubscriptionBlock(block) {
    return !!(block && getSellingPlanInput(block));
  }

  function isSelectedVariantUnavailable() {
    const input = getSelectedVariantInput();
    if (!input) return false;
    return input.hasAttribute('xo-unavailable') || input.hasAttribute('xo-disabled');
  }

  function isVariantNotifyState() {
    return isSelectedVariantUnavailable() ||
      !!$('.main-product__wrapper[xo-unavailable]:not([xo-unavailable="false"])') ||
      !!$('.main-product__wrapper[xo-disabled]:not([xo-disabled="false"])') ||
      !!$('xo-product[xo-unavailable]:not([xo-unavailable="false"])') ||
      !!$('xo-product[xo-disabled]:not([xo-disabled="false"])') ||
      !!$('xo-cart-add[xo-disabled]:not([xo-disabled="false"])');
  }

  function addCSS() {
    if ($('#brissta-purchase-style')) return;

    const style = document.createElement('style');
    style.id = 'brissta-purchase-style';
    style.textContent = `
      .shopify_subscriptions_app_block,
      [id^="shopify-block-"][id*="subscriptions_app_block"],
      [id^="shopify-block-"][id*="subscriptions_app_block_"],
      [id^="shopify-block-"][id*="shopify_subscriptions"],
      .shopify_subscriptions_app_block_label.brissta-hidden {
        display: none !important;
        margin: 0 !important;
        padding: 0 !important;
        height: 0 !important;
        min-height: 0 !important;
        overflow: hidden !important;
      }

      :root {
        --brissta-cta-font-size: 22px;
        --brissta-cta-fill: #73050A;
      }

      #brissta-purchase-type,
      [data-onetime-section-global],
      [data-onetime-submit-global],
      .brissta-primary-submit {
        --brissta-purchase-title-size: clamp(24px, 2vw, 30px);
        --brissta-card-title-size: clamp(18px, 1.35vw, 22px);
        --brissta-card-price-size: calc(var(--brissta-card-title-size) - 2px);
        --brissta-card-detail-size: calc(var(--brissta-card-title-size) - 4px);
      }

      #brissta-purchase-type {
        margin-top: 20px !important;
        margin-right: 0 !important;
        margin-bottom: 12px !important;
        margin-left: 0 !important;
        color: #73050A;
      }

      .shopify-payment-button__buy,
      .shopify-payment-button__buy--hover {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }

      .xo-product-info-content__variants,
      .xo-product-info-content__style-3 .xo-product-info-content__variants {
        padding-bottom: 0 !important;
        margin-bottom: 0 !important;
      }

      #brissta-purchase-type * {
        box-sizing: border-box;
      }

      .brissta-select-offer-section .brissta-purchase-title,
      .brissta-purchase-title {
        display: block;
        margin: 0 !important;
        padding: 0 !important;
        line-height: 1 !important;
        color: #73050A;
        font-family: BehindTheNineties, serif;
        font-size: 18px;
      }
      .brissta-select-offer-section .brissta-purchase-grid {
        margin-top: 0 !important;
      }
      @media (min-width: 990px) {
        .brissta-purchase-title {
          font-size: 20px;
        }
      }

      .brissta-purchase-grid {
        display: grid;
        gap: 12px;
      }

      .brissta-plan-card,
      .brissta-onetime-card {
        width: 100%;
        border: 2px solid #8B1D1D;
        background: transparent;
        color: #8B1D1D;
        padding: 12px 14px;
        text-align: left;
        position: relative;
        transition: background-color 0.18s ease, opacity 0.18s ease;
      }

      .brissta-plan-card {
        cursor: pointer;
      }

      .brissta-plan-card:not(.is-active):hover,
      .brissta-plan-card[aria-pressed="false"]:hover {
        background-color: #73050A;
        color: #f2e6e6;
      }

      .brissta-plan-card:not(.is-active):hover .brissta-plan-badge,
      .brissta-plan-card[aria-pressed="false"]:hover .brissta-plan-badge {
        color: #f2e6e6;
        border-color: #f2e6e6;
      }

      .brissta-plan-card:not(.is-active):hover .brissta-plan-card__price-original,
      .brissta-plan-card[aria-pressed="false"]:hover .brissta-plan-card__price-original {
        opacity: 1;
      }

      .brissta-plan-card.is-active,
      .brissta-onetime-card.is-active {
        background: #DCC0C2;
      }

      .brissta-plan-card[aria-pressed="true"] {
        background: #DCC0C2;
      }

      .brissta-plan-card__head,
      .brissta-onetime-card__head {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        align-items: flex-start;
      }

      .brissta-plan-card__title,
      .brissta-onetime-card__title {
        display: block;
        margin: 0;
        font-family: BehindTheNineties, serif;
        font-size: var(--brissta-card-title-size) !important;
        line-height: 1.08 !important;
      }

      .brissta-plan-card__prices,
      .brissta-onetime-card__prices {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 5px;
        margin-top: 12px;
        font-family: 'Ballinger', sans-serif;
        font-size: var(--brissta-card-price-size);
        line-height: 1.2;
      }

      .brissta-plan-card__prices span,
      .brissta-onetime-card__prices span {
        font-size: var(--brissta-card-price-size) !important;
        line-height: 1.2 !important;
      }

      .brissta-plan-card__per-serving {
        font-family: 'Ballinger', sans-serif;
        font-size: var(--brissta-card-price-size) !important;
        line-height: 1.2;
        opacity: 0.85;
      }

      .brissta-plan-card__price-current,
      .brissta-onetime-card__price-current {
        font-weight: 400;
      }

      .brissta-plan-card__price-original,
      .brissta-onetime-card__price-original {
        opacity: 0.85;
        text-decoration: line-through;
      }

      .brissta-plan-badge {
        flex: 0 0 auto;
        min-width: 88px;
        border: 2px solid #8B1D1D;
        border-radius: 999px;
        padding: 8px 14px;
        text-align: center;
        font-family: BehindTheNineties, serif;
        font-size: clamp(15px, 1.5vw, 18px);
        line-height: 1;
      }

      .brissta-plan-card.is-active .brissta-plan-badge {
        background: #8B1D1D;
        color: #f2e6e6;
        box-shadow: inset 0 0 0 2px #f2e6e6;
      }

      .brissta-plan-card__details,
      .brissta-onetime-card__details {
        display: none;
        margin: 12px 0 0;
        padding: 0;
        list-style: none;
      }

      .brissta-plan-card.is-active .brissta-plan-card__details,
      .brissta-onetime-card.is-active .brissta-onetime-card__details {
        display: grid;
        gap: 4px;
      }

      .brissta-detail-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-family: BehindTheNineties, serif;
        font-size: var(--brissta-card-detail-size) !important;
        line-height: 1.25 !important;
      }

      .brissta-detail-item span {
        font-size: var(--brissta-card-detail-size) !important;
        line-height: 1.25 !important;
      }

      .brissta-detail-item__icon {
        display: inline-block;
        width: 14px;
        flex: 0 0 14px;
        text-align: center;
        font-size: 16px;
        line-height: 1.1;
        color: #8B1D1D;
        align-self: center;
        font-weight: 900;
      }

      .brissta-onetime-trigger {
        display: block;
        justify-self: start;
        margin: 0;
        padding: 0;
        border: 0;
        background: transparent;
        color: #8B1D1D;
        font-family: BehindTheNineties, serif;
        font-size: var(--brissta-card-title-size);
        line-height: 1.08;
        text-decoration: underline;
        cursor: pointer;
      }

      .brissta-onetime-trigger.is-hidden,
      .brissta-onetime-card.is-hidden {
        display: none !important;
      }

      .brissta-onetime-card {
        margin: 0;
      }

      .brissta-onetime-submit {
        margin-top: 12px;
        width: 100%;
      }

      .brissta-onetime-submit.is-hidden {
        display: none !important;
      }

      .brissta-onetime-submit button {
        width: 100%;
        min-height: 0;
        border: 2px solid var(--brissta-cta-fill);
        background: var(--brissta-cta-fill);
        color: #f2e6e6;
        box-shadow: inset 0 0 0 2px #f2e6e6;
        font-family: BehindTheNineties, serif;
        font-size: var(--brissta-cta-font-size) !important;
        line-height: 1;
        padding: 1.6rem 2.6rem;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        text-transform: none;
      }

      /* === Purchase Type (Subscribe & Save / One time purchase) === */
      /* Same inline-style triplet as #brissta-serving-type. */
      #brissta-purchase-type-label.brissta-injected-variant-label {
        font-family: BehindTheNineties, serif;
        font-size: 18px;
        color: #73050A;
        margin-bottom: 10px;
        display: block;
      }
      @media (min-width: 990px) {
        #brissta-purchase-type-label.brissta-injected-variant-label {
          font-size: 20px;
        }
      }
      .brissta-purchase-type-variants {
        width: 100% !important;
        max-width: 100% !important;
        margin-bottom: 12px !important;
        display: block !important;
      }
      .brissta-purchase-type-variants .xo-product-variant,
      .brissta-purchase-type-variants .xo-product-variant__inner {
        width: 100% !important;
        max-width: 100% !important;
        display: block !important;
      }
      .brissta-purchase-type-variants .xo-product-variant__inner-name {
        display: none !important;
      }
      /* Container — same flex layout as Serving Type. flex-direction, gap,
         and per-button widths are mirrored from Serving Type via JS so both
         sections render with identical button dimensions. */
      .brissta-purchase-type-variants .xo-product-variant__inner-value--boxed {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
        max-width: 100%;
        justify-content: flex-start;
        align-items: stretch;
      }
      .brissta-purchase-type-variants .xo-field-radio-3 {
        display: flex;
        box-sizing: border-box;
      }
      .brissta-purchase-type-variants .xo-field-radio-3__label {
        box-sizing: border-box;
      }

      /* === Select Offer section === */
      .brissta-select-offer-section {
        margin: 0 0 12px;
      }
      .brissta-select-offer-grid {
        gap: 14px;
      }
      .brissta-offer-card-wrap {
        position: relative;
        padding-top: 16px;
      }
      .brissta-offer-badge {
        position: absolute;
        top: 16px;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #f2e6e6;
        color: #73050A;
        border: 2px solid #73050A;
        border-radius: 0;
        padding: 4px 14px;
        font-family: BehindTheNineties, serif;
        font-size: clamp(13px, 1.2vw, 16px);
        line-height: 1.1;
        white-space: nowrap;
        z-index: 2;
        text-transform: capitalize;
        transition: background-color 0.18s ease, color 0.18s ease;
      }
      .brissta-offer-badge.is-active,
      .brissta-offer-card-wrap:hover .brissta-offer-badge {
        background: #73050A;
        color: #f2e6e6;
      }
      .brissta-offer-card-wrap:hover .brissta-offer-badge--placeholder {
        visibility: hidden;
      }
      /* Capitalize first letter of every word across these brand-inline elements. */
      .xo-field-radio-3__label,
      .xo-field-radio-3__label > span,
      .xo-field-radio-3.xo-field-radio-3--boxed,
      .xo-field-radio-3.xo-field-radio-3--boxed .xo-field-radio-3__label,
      .xo-field-radio-3.xo-field-radio-3--boxed .xo-field-radio-3__label > span,
      .brissta-plan-card__title,
      .brissta-offer-badge {
        text-transform: capitalize;
      }
      .brissta-offer-badge--placeholder {
        visibility: hidden;
      }
      .brissta-offer-card.brissta-plan-card {
        padding: 20px 18px 16px;
      }

      /* === Why Subscribe? panel (below the submit CTA) === */
      #brissta-why-subscribe {
        margin: 20px 0 0;
        color: #73050A;
        font-family: BehindTheNineties, serif;
      }
      #brissta-why-subscribe .brissta-why-subscribe__title {
        font-family: BehindTheNineties, serif;
        font-size: 18px;
        line-height: 1.05;
        font-weight: 400;
        color: #73050A;
        margin: 0 0 12px;
      }
      @media (min-width: 990px) {
        #brissta-why-subscribe .brissta-why-subscribe__title {
          font-size: 20px;
        }
      }
      #brissta-why-subscribe .brissta-why-subscribe__list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      /* Lock Ballinger on every descendant of the list, including pseudo
         elements, to beat any global universal-selector font override. */
      #brissta-why-subscribe .brissta-why-subscribe__list,
      #brissta-why-subscribe .brissta-why-subscribe__list li,
      #brissta-why-subscribe .brissta-why-subscribe__list li *,
      #brissta-why-subscribe .brissta-why-subscribe__list li::before,
      #brissta-why-subscribe .brissta-why-subscribe__list li::after {
        font-family: 'Ballinger', sans-serif !important;
      }
      #brissta-why-subscribe .brissta-why-subscribe__list li {
        font-size: calc(var(--font-body-scale) * 1.4rem) !important;
        line-height: calc(1 + 0.57 / var(--font-body-scale)) !important;
        color: #73050A;
        position: relative;
        padding-left: 22px;
      }
      @media (min-width: 768px) {
        #brissta-why-subscribe .brissta-why-subscribe__list li {
          font-size: calc(var(--font-body-scale) * 1.6rem) !important;
          line-height: calc(1 + 0.5 / var(--font-body-scale)) !important;
        }
      }
      #brissta-why-subscribe .brissta-why-subscribe__list li::before {
        content: "\\2713";
        position: absolute;
        left: 0;
        top: 0;
        color: #73050A;
      }
      .brissta-offer-card__body {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 14px;
        width: 100%;
      }
      .brissta-offer-card__text {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .brissta-offer-card__pricing {
        flex: 0 0 auto;
        text-align: right;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 2px;
      }
      .brissta-offer-card .brissta-plan-card__prices {
        margin-top: 0;
        justify-content: flex-end;
      }
      .brissta-offer-card__per-coffee {
        font-family: 'Ballinger', sans-serif;
        font-size: var(--brissta-card-price-size) !important;
        line-height: 1.2;
        opacity: 0.85;
        white-space: nowrap;
      }
      @media (max-width: 480px) {
        .brissta-offer-card__body {
          gap: 10px;
        }
        .brissta-offer-badge {
          font-size: 12px;
          padding: 3px 10px;
        }
      }

      .main-product__wrapper[xo-unavailable]:not([xo-unavailable="false"]) .xo-buy-button__cart-add-unavailable {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      xo-cart-add[xo-disabled]:not([xo-disabled="false"]) {
        height: 0 !important;
        min-height: 0 !important;
        overflow: hidden !important;
        padding: 0 !important;
        margin: 0 !important;
      }

      .brissta-notify-copy {
        margin: 12px 0 0 0;
        padding: 0;
        color: #73050A;
        font-family: BehindTheNineties, serif;
        font-size: 16px;
        line-height: 1.25;
        opacity: 1 !important;
      }

      .brissta-oos-spacing .xo-product-info-content__quantity-buy-button {
        gap: 0 !important;
        margin-top: 20px !important;
      }

      #klaviyo-bis-button-container {
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        opacity: 1 !important;
      }

      #klaviyo-bis-button-container button,
      #klaviyo-bis-button-container [class*="kl-private-reset-css"] button,
      #klaviyo-bis-button-container [class*="go"] button {
        width: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        background: #73050A !important;
        background-color: #73050A !important;
        color: #f2e6e6 !important;
        border: 2px solid #73050A !important;
        box-shadow: inset 0 0 0 2px #f2e6e6 !important;
        font-family: BehindTheNineties, serif !important;
        font-size: 22px !important;
        font-weight: 400 !important;
        line-height: 1 !important;
        padding: 16px 26px !important;
        margin: 0 !important;
        min-height: 0 !important;
        height: auto !important;
        text-transform: none !important;
        cursor: pointer !important;
        box-sizing: border-box !important;
        border-radius: var(--buttons-radius-outset, 0) !important;
        letter-spacing: normal !important;
      }

      .xo-buy-button__cart-add-unavailable xo-tooltip.brissta-tooltip-hidden {
        display: none !important;
      }

      .brissta-subscribe-disabled {
        opacity: 0.34 !important;
        pointer-events: none !important;
      }

      .brissta-primary-hidden,
      .brissta-primary-hidden * {
        visibility: hidden !important;
      }

      .brissta-subscribe-host,
      .brissta-subscribe-group,
      .brissta-subscribe-buy-wrap,
      .brissta-subscribe-host xo-product-will-change,
      .brissta-subscribe-host xo-cart-add,
      .brissta-subscribe-host .xo-buy-button__cart-add-add-to-card,
      .brissta-subscribe-host xo-tooltip,
      .brissta-primary-submit {
        display: block !important;
        width: 100% !important;
        height: auto !important;
        min-height: 0 !important;
        overflow: visible !important;
        flex: 0 0 auto !important;
        align-self: flex-start !important;
      }

      .brissta-primary-submit button,
      .brissta-primary-submit .xo-btn {
        width: 100%;
        min-height: 0;
        height: auto !important;
        border: 2px solid var(--brissta-cta-fill) !important;
        box-shadow: inset 0 0 0 2px #f2e6e6;
        background: var(--brissta-cta-fill) !important;
        color: #f2e6e6 !important;
        font-family: BehindTheNineties, serif !important;
        font-size: var(--brissta-cta-font-size) !important;
        line-height: 1 !important;
        padding: 1.6rem 2.6rem !important;
        margin: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        box-sizing: border-box !important;
        text-transform: none !important;
      }

      .main-product .xo-product-info-content__buy-button .brissta-primary-submit .xo-btn,
      .main-product .xo-product-info-content__buy-button .brissta-primary-submit .xo-btn--outline,
      .main-product .xo-product-info-content__buy-button .brissta-primary-submit .xo-btn:hover,
      .main-product .xo-product-info-content__buy-button .brissta-primary-submit .xo-btn--outline:hover,
      .main-product .xo-product-info-content__buy-button .brissta-primary-submit .xo-btn:focus,
      .main-product .xo-product-info-content__buy-button .brissta-primary-submit .xo-btn--outline:focus {
        border: 2px solid var(--brissta-cta-fill) !important;
        border-color: var(--brissta-cta-fill) !important;
        box-shadow: inset 0 0 0 2px #f2e6e6 !important;
        padding: 16px 26px !important;
        margin: 0 !important;
        min-height: 0 !important;
        height: auto !important;
        background: var(--brissta-cta-fill) !important;
        font-size: var(--brissta-cta-font-size) !important;
      }

      .main-product .xo-product-info-content__buy-button .brissta-primary-submit button[type="submit"] {
        border: 2px solid var(--brissta-cta-fill) !important;
        box-shadow: inset 0 0 0 2px #f2e6e6 !important;
        padding: 16px 26px !important;
        margin: 0 !important;
        background: var(--brissta-cta-fill) !important;
        font-size: var(--brissta-cta-font-size) !important;
      }

      .xo-cart-mini-footer__button form[action="/checkout"] .xo-btn,
      .xo-cart-mini-footer__button form[action="/checkout"] .xo-btn--outline,
      .xo-cart-mini-footer__button form[action="/checkout"] .xo-btn:hover,
      .xo-cart-mini-footer__button form[action="/checkout"] .xo-btn--outline:hover,
      .xo-cart-mini-footer__button form[action="/checkout"] .xo-btn:focus,
      .xo-cart-mini-footer__button form[action="/checkout"] .xo-btn--outline:focus {
        display: flex !important;
        width: 100% !important;
        min-height: 0 !important;
        height: auto !important;
        justify-content: center !important;
        align-items: center !important;
        border: 2px solid var(--brissta-cta-fill) !important;
        border-color: var(--brissta-cta-fill) !important;
        box-shadow: inset 0 0 0 2px #f2e6e6 !important;
        padding: 16px 26px !important;
        margin: 0 !important;
        background: var(--brissta-cta-fill) !important;
        background-color: var(--brissta-cta-fill) !important;
        color: #f2e6e6 !important;
        font-family: BehindTheNineties, serif !important;
        font-size: var(--brissta-cta-font-size) !important;
        line-height: 1 !important;
        text-transform: none !important;
        box-sizing: border-box !important;
        transition: none !important;
      }

      .xo-cart-mini-footer__button form[action="/checkout"] .xo-btn::before,
      .xo-cart-mini-footer__button form[action="/checkout"] .xo-btn:hover::before {
        display: none !important;
        content: none !important;
      }

      .xo-cart-mini-footer__button .xo-btn__content,
      .xo-cart-mini-footer__button .xo-btn__text {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin: 0 !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        border-top: 0 !important;
        border-bottom: 0 !important;
        line-height: 1 !important;
      }

      .xo-cart-mini-item__content .xo-quantity {
        margin-top: 5px !important;
      }

      .xo-cart-mini-item__content .xo-quantity--md {
        height: 3rem !important;
      }

      .xo-cart-mini-item__content .xo-quantity__number {
        color: rgb(var(--color-foreground)) !important;
      }

      .brissta-primary-submit .xo-btn::before,
      .brissta-primary-submit .xo-btn:hover::before {
        display: none !important;
        content: none !important;
      }

      .brissta-subscribe-host .shopify-payment-button__group,
      .brissta-subscribe-host .shopify-payment-button__add,
      .brissta-subscribe-host .xo-product-info-content__buy-button {
        width: 100% !important;
        height: auto !important;
        min-height: 0 !important;
        align-items: flex-start !important;
        display: block !important;
        flex: 0 0 auto !important;
        align-self: flex-start !important;
      }

      .brissta-subscribe-group,
      .brissta-subscribe-buy-wrap {
        width: 100% !important;
        height: auto !important;
        min-height: 0 !important;
        display: block !important;
        flex: 0 0 auto !important;
        align-self: flex-start !important;
        overflow: visible !important;
      }

      .brissta-subscribe-group {
        margin-top: 12px !important;
      }

      [data-onetime-section-global] {
        margin-top: 12px;
      }

      .brissta-onetime-slot {
        display: grid;
        gap: 12px;
      }

      .brissta-primary-submit .xo-btn__content,
      .brissta-primary-submit .xo-btn__text,
      .brissta-primary-submit .xo-btn__icon {
        height: auto !important;
        min-height: 0 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 1 !important;
      }

      #xo-main-content,
      .main-product,
      .main-product__content,
      .main-product__content-right,
      .main-product__content-info-content,
      .main-product__content-info-content-style-3 {
        height: auto !important;
        min-height: 0 !important;
        overflow: visible !important;
      }

      @media (max-width: 767px) {
        .brissta-plan-card__head,
        .brissta-onetime-card__head {
          gap: 10px;
        }

        .brissta-plan-badge {
          min-width: 84px;
          padding: 8px 12px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function injectServingTypeLabel() {
    // No-op: #brissta-serving-type heading removed from PDP per design.
    // Any previously-injected element is also removed if present.
    const existing = $('#brissta-serving-type');
    if (existing) existing.remove();
  }

  function hideDynamicCheckoutButtons() {
    $$('.shopify-payment-button__buy').forEach((el) => {
      el.style.display = 'none';
      el.style.visibility = 'hidden';
      el.style.opacity = '0';
      el.style.pointerEvents = 'none';
    });
  }

  function hideSubscriptionAppWrappers() {
    $$('[id^="shopify-block-"][id*="subscriptions_app_block"], [id^="shopify-block-"][id*="subscriptions_app_block_"], [id^="shopify-block-"][id*="shopify_subscriptions"]').forEach((el) => {
      el.style.display = 'none';
      el.style.margin = '0';
      el.style.padding = '0';
      el.style.height = '0';
      el.style.minHeight = '0';
      el.style.overflow = 'hidden';
    });
  }

  function getServingTypeDisplayLabel(rawLabel) {
    const normalized = String(rawLabel || '').trim().toLowerCase();
    if (!isDuoProduct()) return rawLabel;
    if (normalized.includes('stick')) return '56 on-the-go sticks';
    if (normalized.includes('bag')) return '2 bags (56 servings)';
    return rawLabel;
  }

  function syncServingTypeLabels() {
    if (!isDuoProduct()) return;
    const labels = $$('.xo-product-variant__inner-value--boxed .xo-field-radio-3__label');
    let changed = false;

    labels.forEach((label) => {
      const textNode = $('span', label);
      if (!textNode) return;
      const nextText = getServingTypeDisplayLabel(textNode.textContent);
      if (nextText && textNode.textContent !== nextText) {
        textNode.textContent = nextText;
        changed = true;
      }
    });

    if (changed) {
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    }
  }

  function ensureNotifyCopy() {
    $$('.xo-buy-button__cart-add-unavailable').forEach((wrap) => {
      let copy = $('.brissta-notify-copy', wrap);
      if (!copy) {
        copy = document.createElement('p');
        copy.className = 'brissta-notify-copy';
        wrap.insertBefore(copy, wrap.firstChild);
      }
      copy.textContent = "On a coffee break. Click the notify button to get alerted when it's back in stock.";
    });
  }


  function getPerServingCost(planType) {
    const key = getCurrentProductKey();
    const costs = {
      'cafe-au-lait': { 'subscribe-two': '$1.85', 'subscribe-one': '$1.99', 'onetime-two': '$2.68', 'onetime-one': '$2.86' },
      'americano':    { 'subscribe-two': '$1.61', 'subscribe-one': '$1.74', 'onetime-two': '$2.31', 'onetime-one': '$2.49' },
      'duo':          { 'subscribe-two': '$1.99', 'subscribe-one': '$2.14', 'onetime-two': '$2.54', 'onetime-one': '$2.54' },
    };
    let productCosts;
    if (key.includes('cafe-au-lait') || key.includes('cafe au lait')) {
      productCosts = costs['cafe-au-lait'];
    } else if (key.includes('americano')) {
      productCosts = costs['americano'];
    } else if (key.includes('duo')) {
      productCosts = costs['duo'];
    }
    return productCosts ? productCosts[planType] : null;
  }

  function getUnitNoun(unitLabel, quantity) {
    if (unitLabel === 'sticks') return quantity === 1 ? 'carton' : 'cartons';
    if (unitLabel === 'bag') return quantity === 1 ? 'eco-bag' : 'eco-bags';
    return quantity === 1 ? 'unit' : 'units';
  }

  function getServingsNoun(unitLabel, quantity) {
    if (unitLabel === 'sticks') return `${28 * quantity} on-the-go sticks`;
    return `${28 * quantity} servings`;
  }

  function getCadenceText(purchaseType) {
    if (purchaseType === 'onetime') return 'delivered once';
    // Frequency is fixed at 4 weeks for both qty 1 and qty 2 — qty 2 simply
    // ships 2 units per delivery at the same cadence as qty 1.
    return 'delivered every 4 weeks';
  }

  function getOfferBadge(purchaseType, quantity, productKey) {
    if (isMiniPackPDP()) {
      if (purchaseType === 'subscribe') {
        return quantity === 2 ? '15% off' : '10% off';
      }
      // One-time: the 6-pack card shows a "10% Off" badge.
      return quantity === 2 ? '10% Off' : '';
    }
    if (purchaseType === 'subscribe') {
      return quantity === 2 ? '15% off & free shipping' : '10% off & free shipping';
    }
    if (quantity === 2) return 'Save $10 & free shipping';
    if (productKey.includes('cafe-au-lait')) return 'Free shipping';
    return '';
  }

  function computeOfferPrice(pricing, purchaseType, quantity) {
    const productKey = getCurrentProductKey();
    const original = pricing.originalUnitPrice * quantity;

    // americano-mini-pack: fixed offer prices per spec.
    //   Subscribe: 3 Packs $17.99, 6 Packs $33.82
    //   One-time:  3 Packs $19.99 (no discount), 6 Packs $39.98 → $35.98 (10% off)
    if (isMiniPackPDP()) {
      let tpCurrent = original;
      if (purchaseType === 'subscribe') {
        tpCurrent = quantity === 2 ? 33.82 : 17.99;
      } else if (quantity === 2) {
        // One-time 6 Packs: 10% off ($39.98 original → $35.98).
        tpCurrent = 35.98;
      }
      return {
        original: roundMoney(original),
        current: roundMoney(tpCurrent),
        hasDiscount: tpCurrent < original
      };
    }

    let current = original;
    if (purchaseType === 'subscribe') {
      if (quantity === 2) {
        current = original * 0.648;
      } else if (productKey.includes('americano')) {
        current = 48.81;
      } else {
        current = pricing.subscribeOneUnitPrice;
      }
    } else if (quantity === 2) {
      current = original - 10;
    }
    return {
      original: roundMoney(original),
      current: roundMoney(current),
      hasDiscount: current < original
    };
  }

  function computePerCoffee(purchaseType, quantity) {
    let key;
    if (purchaseType === 'onetime') key = quantity === 2 ? 'onetime-two' : 'onetime-one';
    else key = quantity === 2 ? 'subscribe-two' : 'subscribe-one';
    return getPerServingCost(key);
  }

  function getPurchaseTypeMarkup(state) {
    const purchaseType = purchaseTypeOf(state);
    const subscribeChecked = purchaseType === 'subscribe';
    const onetimeChecked = purchaseType === 'onetime';
    const selectedLabel = subscribeChecked ? 'Subscribe & Save' : 'One time purchase';

    // Matches the exact DOM shape of the theme's .xo-product-info-content__variants
    // boxed-variant selector (used by the Serving Type row).
    return `
      <p id="brissta-purchase-type-label" class="brissta-injected-variant-label">Purchase Type</p>
      <div class="xo-product-info-content__variants brissta-purchase-type-variants">
        <div class="xo-product-variant">
          <div class="xo-product-variant__inner">
            <div class="xo-product-variant__inner-name">
              Purchase Type:
              <div class="xo-product-variant__inner-selected-value" xo-name="Purchase Type">${selectedLabel}</div>
            </div>
            <div class="xo-product-variant__inner-value xo-product-variant__inner-value--boxed">
              <label class="xo-field-radio-3 xo-field-radio-3--boxed" style="--icon-size: 1.6rem;">
                <input class="xo-field-radio-3__input brissta-purchase-type-input" type="radio" name="brissta-purchase-type" xo-label="Subscribe & Save" value="subscribe" data-purchase-type="subscribe"${subscribeChecked ? ' checked' : ''}>
                <span class="xo-field-radio-3__label" tabindex="0" role="button" aria-label="Subscribe & Save">
                  <span>Subscribe &amp; Save</span>
                </span>
              </label>
              <label class="xo-field-radio-3 xo-field-radio-3--boxed" style="--icon-size: 1.6rem;">
                <input class="xo-field-radio-3__input brissta-purchase-type-input" type="radio" name="brissta-purchase-type" xo-label="One time purchase" value="onetime" data-purchase-type="onetime"${onetimeChecked ? ' checked' : ''}>
                <span class="xo-field-radio-3__label" tabindex="0" role="button" aria-label="One time purchase">
                  <span>One time purchase</span>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function getOfferCardMarkup(pricing, unitLabel, purchaseType, quantity, isActive) {
    const productKey = getCurrentProductKey();
    const badge = getOfferBadge(purchaseType, quantity, productKey);
    const price = computeOfferPrice(pricing, purchaseType, quantity);
    const perCoffee = computePerCoffee(purchaseType, quantity);
    const unitNoun = getUnitNoun(unitLabel, quantity);
    const cadence = getCadenceText(purchaseType);
    const servings = getServingsNoun(unitLabel, quantity);
    const targetState = buildState(purchaseType, quantity);

    // americano-mini-pack: label offers by pack count — "3 Packs" (1 unit /
    // 9 servings) and "6 Packs" (2 units / 18 servings) — instead of the
    // generic "{qty} units / {28×qty} servings".
    const cardTitle = isMiniPackPDP() ? `${3 * quantity} Packs` : `${quantity} ${unitNoun}`;
    const cardServings = isMiniPackPDP() ? `${9 * quantity} servings` : servings;

    const badgeActiveClass = isActive ? ' is-active' : '';
    return `
      <div class="brissta-offer-card-wrap">
        ${badge ? `<span class="brissta-offer-badge${badgeActiveClass}">${badge}</span>` : `<span class="brissta-offer-badge brissta-offer-badge--placeholder${badgeActiveClass}" aria-hidden="true"></span>`}
        <button type="button"
          class="brissta-plan-card brissta-offer-card${isActive ? ' is-active' : ''}"
          data-state="${targetState}"
          data-purchase-type="${purchaseType}"
          data-quantity="${quantity}"
          aria-pressed="${isActive ? 'true' : 'false'}">
          <span class="brissta-offer-card__body">
            <span class="brissta-offer-card__text">
              <span class="brissta-plan-card__title">${cardTitle}</span>
              <span class="brissta-plan-card__per-serving">${cardServings} ${cadence}</span>
            </span>
            <span class="brissta-offer-card__pricing">
              <span class="brissta-plan-card__prices">
                ${price.hasDiscount ? `<span class="brissta-plan-card__price-original">${money(price.original)}</span>` : ''}
                <span class="brissta-plan-card__price-current">${money(price.current)}</span>
              </span>
              ${perCoffee ? `<span class="brissta-offer-card__per-coffee">(${perCoffee} / coffee)</span>` : ''}
            </span>
          </span>
        </button>
      </div>
    `;
  }

  function getSelectOfferMarkup(pricing, unitLabel, state) {
    const purchaseType = purchaseTypeOf(state);

    if (isTargetPDP()) {
      const activeIdx = offerIndexOf(state);
      return `
        <div class="brissta-select-offer-section">
          <span class="brissta-purchase-title">Select Offer</span>
          <div class="brissta-purchase-grid brissta-select-offer-grid">
            ${getVisibleOfferIndices().map((idx) => getThreeOfferCardMarkup(pricing, unitLabel, purchaseType, idx, idx === activeIdx)).join('')}
          </div>
        </div>
      `;
    }

    const quantity = quantityOf(state);
    const cardQty1 = getOfferCardMarkup(pricing, unitLabel, purchaseType, 1, quantity === 1);
    const cardQty2 = getOfferCardMarkup(pricing, unitLabel, purchaseType, 2, quantity === 2);
    // Mini-pack lists small→large ("3 Packs" then "6 Packs"); other legacy
    // PDPs keep the original large→small (qty 2 then qty 1) order.
    const legacyCards = isMiniPackPDP()
      ? `${cardQty1}\n${cardQty2}`
      : `${cardQty2}\n${cardQty1}`;
    return `
      <div class="brissta-select-offer-section">
        <span class="brissta-purchase-title">Select Offer</span>
        <div class="brissta-purchase-grid brissta-select-offer-grid">
          ${legacyCards}
        </div>
      </div>
    `;
  }

  // -----------------------------------------------------------------------
  // 3-offer card rendering — used on target PDPs.
  // -----------------------------------------------------------------------
  function getThreeOfferCardTitle(unitLabel, offerIdx) {
    // Offer 0 has a fixed marketing label — "3 Travel Packs" for the
    // americano-sticks PDP, "Mini Eco-Bag" for the bag PDPs.
    if (offerIdx === 0) {
      const path = (window.location.pathname || '').toLowerCase();
      if (path.includes('americano-sticks')) return '3 Travel Packs';
      return 'Mini Eco-Bag';
    }
    // Offers 1 + 2 use variant title × quantity.
    const offer = OFFER_CONFIG[offerIdx];
    const radio = getServingTypeRadioForOfferLogicalIndex(offer.variantIndex);
    const info = getVariantInfoForOfferLogicalIndex(offer.variantIndex);
    let label = (
      (radio && radio.getAttribute('xo-label')) ||
      (info && info.title) ||
      ''
    ).trim();
    if (!label) {
      if (unitLabel === 'sticks') label = 'Carton';
      else label = 'Eco-Bag';
    }
    if (offer.quantity === 1) return `1 ${label}`;
    const plural = /s$/i.test(label) ? label : `${label}s`;
    return `${offer.quantity} ${plural}`;
  }

  function getThreeOfferServings(unitLabel, offerIdx) {
    if (offerIdx === 0) {
      // Café Au Lait's Mini Eco-Bag is 10 servings; other products' offer 0 is 9.
      var path = (window.location.pathname || '').toLowerCase();
      return path.indexOf('cafe-au-lait') !== -1 ? '10 servings' : '9 servings';
    }
    if (offerIdx === 1) return '28 servings';
    if (offerIdx === 2) return '56 servings';
    return '';
  }

  function getThreeOfferBadge(purchaseType, offerIdx) {
    if (purchaseType === 'subscribe') {
      if (offerIdx === 0) return '10% Off';
      if (offerIdx === 1) return '10% Off & Free Shipping';
      if (offerIdx === 2) return '15% Off & Free Shipping';
      return '';
    }
    if (offerIdx === 1) return 'Free Shipping';
    if (offerIdx === 2) return 'Save $10 & Free Shipping';
    return '';
  }

  // Discount rules per offer (matches the three target PDPs):
  //   Subscribe + offer 0 (3 packs / 3 mini eco-bags) → 10% off
  //   Subscribe + offer 1 (1 carton / 1 eco-bag)     → 10% off
  //   Subscribe + offer 2 (2 cartons / 2 eco-bags)   → 15% off
  //   OTP      + offer 0                              → no discount
  //   OTP      + offer 1                              → no discount
  //   OTP      + offer 2                              → flat $10 off
  function computeThreeOfferPrice(pricing, purchaseType, offerIdx) {
    const offer = OFFER_CONFIG[offerIdx];
    // Look up the real product variant by title pattern (small / large)
    // rather than by DOM position, so admin variant order doesn't matter.
    const variantInfo = getVariantInfoForOfferLogicalIndex(offer.variantIndex);
    // Shopify variant prices are in cents — convert to dollars. Fall back
    // to the legacy pricing.originalUnitPrice when JSON isn't available.
    const unitPriceDollars = variantInfo
      ? Number(variantInfo.price) / 100
      : (pricing && pricing.originalUnitPrice) || 0;
    const original = unitPriceDollars * offer.quantity;
    let current = original;
    if (purchaseType === 'subscribe') {
      const discount = offerIdx === 2 ? 0.15 : 0.10;
      current = original * (1 - discount);
    } else if (offerIdx === 2) {
      current = Math.max(0, original - 10);
    }
    // Hard-coded current-price overrides per spec — offer 2 SUBSCRIBE only.
    if (purchaseType === 'subscribe' && offerIdx === 2) {
      const path = (window.location.pathname || '').toLowerCase();
      if (path.includes('cafe-au-lait')) current = 115.04;
      else if (path.includes('americano')) current = 94.74;
    }
    return {
      original: roundMoney(original),
      current: roundMoney(current),
      hasDiscount: current < original
    };
  }

  // Servings per offer — fixed: 9 / 28 / 56 across all three target PDPs.
  function getServingsCount(offerIdx) {
    return offerIdx === 0 ? 9 : offerIdx === 1 ? 28 : 56;
  }

  function computePerCupCost(currentPriceDollars, offerIdx, purchaseType) {
    // Marketing-approved per-cup overrides:
    //   ┌────────────────┬──────────┬──────────┐
    //   │ Product family │ Subscribe│ OTP      │
    //   ├────────────────┼──────────┼──────────┤
    //   │ CAL  offer 0/1 │ $2.57    │ $2.86    │
    //   │ CAL  offer 2   │ $2.41    │ dynamic  │
    //   │ Amer offer 0/1 │ $2.24    │ $2.49    │
    //   │ Amer offer 2   │ $2.11    │ dynamic  │
    //   └────────────────┴──────────┴──────────┘
    const path = (window.location.pathname || '').toLowerCase();
    const isCAL = path.includes('cafe-au-lait');
    const isAmer = path.includes('americano');
    const isSubscribe = purchaseType === 'subscribe';

    if (offerIdx === 0 || offerIdx === 1) {
      if (isCAL)  return money(isSubscribe ? 2.57 : 2.86);
      if (isAmer) return money(isSubscribe ? 2.24 : 2.49);
    }
    if (offerIdx === 2 && isSubscribe) {
      if (isCAL)  return money(2.41);
      if (isAmer) return money(2.11);
    }
    // Fallback: dynamic — current price ÷ servings count for that offer.
    const servings = getServingsCount(offerIdx);
    if (!servings || !isFinite(currentPriceDollars)) return '';
    const perCup = currentPriceDollars / servings;
    return money(roundMoney(perCup));
  }

  function getThreeOfferCardMarkup(pricing, unitLabel, purchaseType, offerIdx, isActive) {
    const title = getThreeOfferCardTitle(unitLabel, offerIdx);
    const servings = getThreeOfferServings(unitLabel, offerIdx);
    const badge = getThreeOfferBadge(purchaseType, offerIdx);
    const price = computeThreeOfferPrice(pricing, purchaseType, offerIdx);
    const perCup = computePerCupCost(price.current, offerIdx, purchaseType);
    const cadence = purchaseType === 'subscribe' ? 'delivered every 4 weeks' : 'delivered once';
    const targetState = `${purchaseType}-${offerIdx}`;
    const badgeActiveClass = isActive ? ' is-active' : '';
    const badgeMarkup = badge
      ? `<span class="brissta-offer-badge${badgeActiveClass}">${badge}</span>`
      : `<span class="brissta-offer-badge brissta-offer-badge--placeholder${badgeActiveClass}" aria-hidden="true"></span>`;
    // For Subscribe: show struck-through original + current + per-cup.
    // For OTP: only show original on offer 2 (the $10-off "2 of variant 2"
    // promo) — the other OTP offers have no discount, so no comparison.
    const showOriginal = purchaseType === 'subscribe' || (purchaseType === 'onetime' && offerIdx === 2);
    return `
      <div class="brissta-offer-card-wrap">
        ${badgeMarkup}
        <button type="button"
          class="brissta-plan-card brissta-offer-card${isActive ? ' is-active' : ''}"
          data-state="${targetState}"
          data-purchase-type="${purchaseType}"
          data-offer-index="${offerIdx}"
          aria-pressed="${isActive ? 'true' : 'false'}">
          <span class="brissta-offer-card__body">
            <span class="brissta-offer-card__text">
              <span class="brissta-plan-card__title">${title}</span>
              <span class="brissta-plan-card__per-serving">${servings} ${cadence}</span>
            </span>
            <span class="brissta-offer-card__pricing">
              <span class="brissta-plan-card__prices">
                ${showOriginal ? `<span class="brissta-plan-card__price-original">${money(price.original)}</span>` : ''}
                <span class="brissta-plan-card__price-current">${money(price.current)}</span>
              </span>
              ${perCup ? `<span class="brissta-offer-card__per-coffee">(${perCup} / coffee)</span>` : ''}
            </span>
          </span>
        </button>
      </div>
    `;
  }

  function getCardMarkup(pricing, unitLabel, state) {
    return `
      ${getPurchaseTypeMarkup(state)}
      ${getSelectOfferMarkup(pricing, unitLabel, state)}
    `;
  }

  function getWidget() {
    return $('#brissta-purchase-type');
  }

  function renderWidget(block) {
    const existing = getWidget();
    if (existing) existing.remove();

    const primaryButtonContainer = getPrimaryButtonContainer();
    const subscribeAddHost = getSubscribeAddHost(primaryButtonContainer);
    if (!primaryButtonContainer || !subscribeAddHost || !subscribeAddHost.parentNode) return;

    const widget = document.createElement('div');
    widget.id = 'brissta-purchase-type';
    const state = normalizeState(getSavedState());
    const pricing = getPricing(block);
    const unitLabel = getUnitLabel();

    // Purchase-type and offer cards both live INSIDE the widget. The widget
    // mounts next to the Subscribe button — a location Shopify doesn't
    // rebuild on variant change — so the elements are immune to the section
    // AJAX wipes that previously made them disappear when they were moved
    // into the variants area.
    widget.innerHTML = getCardMarkup(pricing, unitLabel, state);
    subscribeAddHost.parentNode.insertBefore(widget, subscribeAddHost);

    bindWidgetEvents(block, widget);

    // Remove any stale purchase-type copies left in the variants area from
    // older code paths (one-time cleanup; harmless if nothing is there).
    document.querySelectorAll('#brissta-purchase-type-label').forEach((el) => {
      if (!widget.contains(el)) el.remove();
    });
    document.querySelectorAll('.brissta-purchase-type-variants').forEach((el) => {
      if (!widget.contains(el)) el.remove();
    });

    // Clean up any legacy one-time section/submit from previous versions
    const legacyOnetimeSection = $('[data-onetime-section-global]');
    if (legacyOnetimeSection) legacyOnetimeSection.remove();
    const legacyOnetimeSubmit = $('[data-onetime-submit-global]');
    if (legacyOnetimeSubmit) legacyOnetimeSubmit.remove();
    syncPrimaryButtons(block, widget);
    syncPurchaseTypeLabelSize();
    schedulePurchaseTypeLabelSyncRetries();
  }

  // Mirror font-size + flex-direction + gap from Serving Type's container to
  // Purchase Type's container, and also copy the Serving Type HEADING's
  // computed font-size to #brissta-purchase-type-label and .brissta-purchase-title
  // so every section title (Serving / Purchase / Select Offer) renders at the
  // exact same size as #brissta-serving-type, whatever the theme resolves it to.
  function syncPurchaseTypeLabelSize() {
    const servingHeading = document.getElementById('brissta-serving-type');
    if (!servingHeading) return;

    // Heading-size mirror — apply Serving Type's rendered font-size to every
    // brand-inline section heading (Purchase Type label, Select Offer title,
    // Why Subscribe? title). Also link the offer-card price + per-coffee
    // font-sizes to the same primary size so they scale together.
    const headingFontSize = window.getComputedStyle(servingHeading).fontSize;
    if (headingFontSize) {
      // Primary-size group: section headings + offer-card prices.
      const primaryTargets = [
        document.getElementById('brissta-purchase-type-label'),
        ...document.querySelectorAll('.brissta-purchase-title'),
        ...document.querySelectorAll('.brissta-why-subscribe__title'),
        ...document.querySelectorAll('.brissta-offer-card .brissta-plan-card__price-original'),
        ...document.querySelectorAll('.brissta-offer-card .brissta-plan-card__price-current'),
      ];
      primaryTargets.forEach((el) => {
        if (el) el.style.setProperty('font-size', headingFontSize, 'important');
      });

    }

    // Text hierarchy anchored off .brissta-plan-card__title:
    //   title (unchanged) → badge (title - 1.5px) → per-serving/per-coffee (title - 3px)
    const sampleTitle = document.querySelector('.brissta-offer-card .brissta-plan-card__title');
    if (sampleTitle) {
      const titleNum = parseFloat(window.getComputedStyle(sampleTitle).fontSize);
      if (Number.isFinite(titleNum) && titleNum > 3) {
        const badgeSize = (titleNum - 1.5) + 'px';
        const subSize = (titleNum - 3) + 'px';
        document.querySelectorAll('.brissta-offer-badge').forEach((el) => {
          el.style.setProperty('font-size', badgeSize, 'important');
        });
        [
          ...document.querySelectorAll('.brissta-offer-card .brissta-plan-card__per-serving'),
          ...document.querySelectorAll('.brissta-offer-card__per-coffee'),
        ].forEach((el) => {
          el.style.setProperty('font-size', subSize, 'important');
        });
      }
    }

    const servingGroup = servingHeading.nextElementSibling;
    if (
      !servingGroup ||
      !servingGroup.classList.contains('xo-product-variant__inner-value--boxed')
    ) {
      return;
    }

    const purchaseGroup = document.querySelector(
      '.brissta-purchase-type-variants .xo-product-variant__inner-value--boxed'
    );
    if (!purchaseGroup) return;

    const varFontSize = servingGroup.style.getPropertyValue(
      '--brissta-serving-type-button-font-size'
    );
    const varWidth = servingGroup.style.getPropertyValue(
      '--brissta-serving-type-button-width'
    );
    if (varFontSize) {
      purchaseGroup.style.setProperty(
        '--brissta-serving-type-button-font-size',
        varFontSize
      );
    }
    if (varWidth) {
      purchaseGroup.style.setProperty(
        '--brissta-serving-type-button-width',
        varWidth
      );
    }
    purchaseGroup.style.flexDirection =
      servingGroup.style.flexDirection || 'row';
    purchaseGroup.style.gap = servingGroup.style.gap || '10px';

    // Mirror per-button sizing (flex, width, min/max-width) and per-label
    // sizing (font-size, width, min/max-width) from Serving Type onto Purchase
    // Type so the two rows render with identical button footprints.
    const servingBtns = Array.from(servingGroup.querySelectorAll('.xo-field-radio-3'));
    const purchaseBtns = Array.from(purchaseGroup.querySelectorAll('.xo-field-radio-3'));
    if (!servingBtns.length || !purchaseBtns.length) return;
    const sampleBtn = servingBtns[0];
    const sampleLabel = sampleBtn.querySelector('.xo-field-radio-3__label');
    const sampleSpan = sampleLabel ? sampleLabel.querySelector('span') : null;

    purchaseBtns.forEach((btn) => {
      btn.style.flex = sampleBtn.style.flex || '';
      btn.style.width = sampleBtn.style.width || '';
      btn.style.minWidth = sampleBtn.style.minWidth || '';
      btn.style.maxWidth = sampleBtn.style.maxWidth || '';

      const label = btn.querySelector('.xo-field-radio-3__label');
      if (!label || !sampleLabel) return;
      label.style.fontSize = sampleLabel.style.fontSize || '';
      label.style.width = sampleLabel.style.width || '';
      label.style.minWidth = sampleLabel.style.minWidth || '';
      label.style.maxWidth = sampleLabel.style.maxWidth || '';
      label.style.boxSizing = sampleLabel.style.boxSizing || 'border-box';

      const span = label.querySelector('span');
      if (span && sampleSpan) {
        span.style.fontSize = sampleSpan.style.fontSize || '';
        span.style.lineHeight = sampleSpan.style.lineHeight || '';
      }
    });
  }

  function normalizeState(state) {
    const s = String(state || '');
    if (isTargetPDP()) {
      // 3-offer states: subscribe-0|1|2, onetime-0|1|2
      const parts = s.split('-');
      const type = parts[0] === 'onetime' ? 'onetime' : 'subscribe';
      const idx = parseInt(parts[1], 10);
      let safeIdx = !isNaN(idx) && OFFER_CONFIG[idx] ? idx : defaultOfferIndex();
      if (!isOfferVisible(safeIdx)) safeIdx = defaultOfferIndex();
      return `${type}-${safeIdx}`;
    }
    if (s === 'onetime') return 'onetime-one';
    return ['subscribe-two', 'subscribe-one', 'onetime-two', 'onetime-one'].includes(s) ? s : 'subscribe-one';
  }

  function syncPrimaryButtons() {
    const state = normalizeState(getSavedState());
    const purchaseType = purchaseTypeOf(state);
    const isOnetime = purchaseType === 'onetime';
    const submitButton = getManagedPrimarySubmit();
    const submitContainer = getPrimaryButtonContainer();

    if (submitButton) {
      submitButton.textContent = isOnetime ? 'Add to cart' : 'Subscribe';
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-disabled');
      submitButton.classList.remove('brissta-subscribe-disabled');
      submitButton.onclick = async function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        try {
          await addCurrentProductToCart();
        } catch (error) {
          console.error(error);
        }
      };
    }

    if (submitContainer) {
      submitContainer.classList.add('brissta-primary-submit');
      submitContainer.classList.remove('brissta-subscribe-disabled');
    }

    const submitHost = getSubscribeAddHost(submitContainer);
    if (submitHost) {
      submitHost.classList.add('brissta-subscribe-host');
    }

    const submitGroup = submitContainer ? submitContainer.closest('.shopify-payment-button__group') : null;
    if (submitGroup) {
      submitGroup.classList.add('brissta-subscribe-group');
    }

    const submitBuyWrap = submitContainer ? submitContainer.closest('.xo-product-info-content__buy-button') : null;
    if (submitBuyWrap) {
      submitBuyWrap.classList.add('brissta-subscribe-buy-wrap');
    }

    // Strip any leftover one-time submit/section from prior versions
    const legacyOnetimeSubmit = $('[data-onetime-submit-global]');
    if (legacyOnetimeSubmit) legacyOnetimeSubmit.remove();
    const legacyOnetimeSection = $('[data-onetime-section-global]');
    if (legacyOnetimeSection) legacyOnetimeSection.remove();
  }

  // Patch checkValidity on every product form to always return true.
  // These forms all have novalidate so browser validation is never
  // intended. Apps (subscriptions, etc.) inject required fields that
  // cause the theme's handleAddToCart to bail without preventDefault().
  function patchAllFormValidity() {
    const form = getProductForm();
    if (!form || form._brisstaValidityPatched) return;
    form.checkValidity = function () { return true; };
    form.reportValidity = function () { return true; };
    form._brisstaValidityPatched = true;
  }

  function setCurrentState(block, nextState, widget) {
    const state = normalizeState(nextState);
    saveState(state);

    setFormQuantity(quantityOf(state));
    setPurchaseMode(block, purchaseTypeOf(state));
    if (isTargetPDP()) {
      selectServingTypeVariant(variantIndexOfState(state));
      updateOfferImageVisibility(state);
      // Selecting a different variant can trigger Shopify's section AJAX
      // refresh which rebuilds the image grid, wiping our display:none.
      // Re-apply at staggered delays so whichever moment the new DOM
      // settles, the filter sticks.
      [120, 350, 900, 1800].forEach(function (d) {
        setTimeout(function () { updateOfferImageVisibility(getSavedState()); }, d);
      });
    }

    refreshWidget(block, widget);
  }

  // Per-offer image WHITELIST (ordered) for americano-sticks.
  // Each entry is a substring matched against the wrapper's outerHTML —
  // covers src, srcset, data-src, data-srcset, xo-zoom-src, etc.
  const OFFER_IMAGE_SHOW = {
    'americano-sticks': {
      1: [
        'americano-hero-shopify',
        'Screenshot_2026-03-16_at_3.29.11_PM',
        'americano-tray-shopify',
        'Screenshot_2026-03-16_at_3.27.16_PM',
        'brissta-americano-on-the-go-sticks-nutrition-facts'
      ],
      2: [
        'americano-hero-shopify',
        'Screenshot_2026-03-16_at_3.29.11_PM',
        'americano-tray-shopify',
        'Screenshot_2026-03-16_at_3.27.16_PM',
        'brissta-americano-on-the-go-sticks-nutrition-facts'
      ]
    }
  };

  // Selectors for the per-image wrappers across desktop stacked grid, mobile
  // wholesale-style stage, fallback Shopify carousel, and the brissta-pdp-media
  // (cafe-au-lait/americano parent) variants.
  const PRODUCT_IMAGE_WRAPPER_SELECTORS = [
    '.product-info-media-stacked__carousel-right-content',
    '.product-info-media-carousel__image',
    '.product-info-media-carousel__gallery-item',
    '.brissta-pdp-media__stack-item',
    '.brissta-pdp-media__slide',
    '.product-info-media-stacked__thumb-item',
    '.featured-products__img'
  ];

  // Find the index of the show-list entry that matches a wrapper's markup.
  // Lower index = earlier position in the list. Returns -1 if no match.
  function findShowOrderIndex(wrap, showList) {
    const html = (wrap.outerHTML || '').toLowerCase();
    for (let i = 0; i < showList.length; i++) {
      if (html.indexOf(showList[i].toLowerCase()) !== -1) return i;
    }
    return -1;
  }

  function reorderAndFilterContainer(container, showList) {
    if (!container) return;
    // Operate on direct children that are image-wrappers OR contain them.
    const children = Array.from(container.children);
    const ordered = []; // [{idx, child}]
    const hidden = [];
    children.forEach(function (child) {
      // Each child should ITSELF be an image wrapper (matches one of our
      // wrapper selectors). For containers like the mobile stage, child is
      // .featured-products__img directly.
      const idx = findShowOrderIndex(child, showList);
      if (idx >= 0) {
        ordered.push({ idx: idx, child: child });
        child.style.display = '';
      } else {
        hidden.push(child);
        child.style.display = 'none';
      }
    });
    // Sort by show-list position.
    ordered.sort(function (a, b) { return a.idx - b.idx; });
    // Reattach in the desired order. Hidden ones go to the end (still in DOM).
    ordered.forEach(function (entry) { container.appendChild(entry.child); });
    hidden.forEach(function (child) { container.appendChild(child); });
  }

  function syncMobileStageActiveAndDots(stage, opts) {
    if (!stage) return;
    const forceReset = !!(opts && opts.resetActive);
    const items = Array.from(stage.querySelectorAll('.featured-products__img[data-media-id]'));
    if (!items.length) return;
    const visible = items.filter(function (it) { return it.style.display !== 'none'; });
    if (!visible.length) return;

    // Preserve the user's current selection across retries — only reset
    // active to the first visible item when caller explicitly asks (offer
    // change) OR when there's no valid active item in the visible set.
    let activeImg = null;
    if (!forceReset) {
      const currentActive = items.find(function (it) { return it.classList.contains('is-active'); });
      if (currentActive && visible.indexOf(currentActive) !== -1) {
        activeImg = currentActive;
      }
    }
    if (!activeImg) activeImg = visible[0];
    items.forEach(function (it) { it.classList.toggle('is-active', it === activeImg); });

    // Reorder and toggle dots to match item order.
    const dotsContainer = stage.querySelector('.featured-products__media-dots');
    if (dotsContainer) {
      const dots = Array.from(dotsContainer.querySelectorAll('[data-fp-dot]'));
      items.forEach(function (it) {
        const dot = dots.find(function (d) { return d.getAttribute('data-media-id') === it.getAttribute('data-media-id'); });
        if (dot) {
          dot.style.display = it.style.display === 'none' ? 'none' : '';
          dotsContainer.appendChild(dot);
        }
      });
      const activeId = activeImg.getAttribute('data-media-id');
      dots.forEach(function (d) { d.classList.toggle('is-active', d.getAttribute('data-media-id') === activeId); });
    }
  }

  let _lastAppliedOfferIdx = null;
  function updateOfferImageVisibility(state) {
    const path = (window.location.pathname || '').toLowerCase();
    let showRules = null;
    Object.keys(OFFER_IMAGE_SHOW).forEach(function (handle) {
      if (path.includes('/products/' + handle)) showRules = OFFER_IMAGE_SHOW[handle];
    });
    if (!showRules) return;
    const offerIdx = offerIndexOf(state);
    const showList = showRules[offerIdx];
    if (!showList || !showList.length) return;
    // Only force-reset the active mobile slide to the first visible image
    // when the offer actually changes. On retries (same offer index, fired
    // by setTimeout to catch post-AJAX DOM rebuilds), we preserve whatever
    // image the user manually navigated to.
    const offerChanged = _lastAppliedOfferIdx !== offerIdx;
    _lastAppliedOfferIdx = offerIdx;

    // Desktop stacked grid container holds all .product-info-media-stacked__carousel-right-content.
    const desktopGrid = document.querySelector('.product-info-media-stacked__carousel-right');
    reorderAndFilterContainer(desktopGrid, showList);

    // Mobile (target PDPs): featured-products media-stage with featured-products__img children.
    const mobileStage = document.querySelector('.brissta-mobile-stage .featured-products__media-stage');
    if (mobileStage) {
      // The media-stage also contains nav buttons + dots — only reorder/filter the .featured-products__img children.
      const imgs = Array.from(mobileStage.querySelectorAll(':scope > .featured-products__img'));
      const ordered = [];
      const hidden = [];
      imgs.forEach(function (child) {
        const idx = findShowOrderIndex(child, showList);
        if (idx >= 0) {
          ordered.push({ idx: idx, child: child });
          child.style.display = '';
        } else {
          hidden.push(child);
          child.style.display = 'none';
        }
      });
      ordered.sort(function (a, b) { return a.idx - b.idx; });
      // Reorder images at the START of the stage in desired order. Nav
      // buttons + dots are absolutely positioned; their DOM position
      // doesn't affect their visual placement.
      // Insert in REVERSE order using insertBefore(firstChild) so that
      // after all inserts, the first item in `ordered` ends up first.
      for (let i = ordered.length - 1; i >= 0; i--) {
        mobileStage.insertBefore(ordered[i].child, mobileStage.firstChild);
      }
      hidden.forEach(function (child) { mobileStage.appendChild(child); });
      syncMobileStageActiveAndDots(mobileStage, { resetActive: offerChanged });
    }

    // Fallback Shopify carousel slides (when not using brissta-mobile-stage).
    const shopifyCarousel = document.querySelector('.product-info-media-carousel__carousel');
    if (shopifyCarousel) {
      const slides = Array.from(shopifyCarousel.querySelectorAll(':scope > xo-carousel-slide, :scope .product-info-media-carousel__image'));
      slides.forEach(function (slide) {
        const idx = findShowOrderIndex(slide, showList);
        slide.style.display = idx >= 0 ? '' : 'none';
      });
    }

    // brissta-pdp-media (cafe-au-lait / americano parent) — only filter, the
    // order is already controlled by Liquid lists in those snippets.
    const brisstaStacks = Array.from(document.querySelectorAll('.brissta-pdp-media__stack'));
    brisstaStacks.forEach(function (stack) {
      const items = Array.from(stack.querySelectorAll(':scope > .brissta-pdp-media__stack-item'));
      items.forEach(function (it) {
        const idx = findShowOrderIndex(it, showList);
        it.style.display = idx >= 0 ? '' : 'none';
      });
    });
  }

  function refreshWidget(block, widget) {
    const pricing = getPricing(block);
    const state = normalizeState(getSavedState());
    const unitLabel = getUnitLabel();

    const purchaseTypeAlreadyMounted = isTargetPDP()
      && document.querySelector('#brissta-purchase-type-label')
      && document.querySelector('.brissta-purchase-type-variants');

    if (purchaseTypeAlreadyMounted) {
      // Targeted refresh — replace only the Select Offer section + sync the
      // existing purchase-type radios' checked state. Avoids tearing down
      // the moved-out Purchase Type DOM on every offer toggle (which was
      // causing the visible glitch / re-flow on every click).
      const offerSection = widget.querySelector('.brissta-select-offer-section');
      const newOfferHtml = getSelectOfferMarkup(pricing, unitLabel, state).trim();
      const tmp = document.createElement('div');
      tmp.innerHTML = newOfferHtml;
      const newSection = tmp.firstElementChild;
      if (offerSection && newSection) {
        offerSection.replaceWith(newSection);
      } else if (newSection) {
        widget.appendChild(newSection);
      }
      bindOfferCardEvents(block, widget);
      // Update the existing purchase-type radios' checked state without
      // re-rendering them — preserves DOM identity and avoids flash.
      const purchaseType = purchaseTypeOf(state);
      document.querySelectorAll('.brissta-purchase-type-input').forEach((input) => {
        input.checked = input.dataset.purchaseType === purchaseType;
      });
    } else {
      // Cold render needed — purchase-type DOM is missing (e.g., Shopify
      // section AJAX wiped the variants area). Tear down the old widget
      // and re-mount via renderWidget which uses the atomic-extract path.
      const w = getWidget();
      if (w) w.remove();
      renderWidget(block);
      return;
    }

    syncPrimaryButtons(block, widget);
    syncPurchaseTypeLabelSize();
    schedulePurchaseTypeLabelSyncRetries();
  }

  // Bind offer-card click handlers (idempotent) so we can re-call after
  // replacing only the Select Offer section without doubling listeners.
  function bindOfferCardEvents(block, widget) {
    $$('.brissta-offer-card[data-state]', widget).forEach((button) => {
      if (button._brisstaBound) return;
      button._brisstaBound = true;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        setCurrentState(block, button.dataset.state, widget);
      });
    });
  }

  // Re-sync on viewport changes, font loads, and any time the theme's
  // syncServingTypeButtonWidths() mutates the Serving Type container's inline
  // style or CSS vars.
  function runAllPurchaseSyncs() {
    syncPurchaseTypeLabelSize();
    syncWhySubscribeFontSize();
  }

  function schedulePurchaseTypeLabelSyncRetries() {
    [60, 200, 600, 1200, 2400].forEach((delay) => {
      setTimeout(runAllPurchaseSyncs, delay);
    });
  }

  if (!window._brisstaPurchaseTypeResizeBound) {
    window._brisstaPurchaseTypeResizeBound = true;

    let resizeFrame = null;
    window.addEventListener('resize', () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        runAllPurchaseSyncs();
      });
    });

    // Watch the Serving Type container for inline-style mutations — the
    // theme's JS updates it on font load, resize, DOM change, etc.
    const attachServingTypeStyleObserver = () => {
      const servingHeading = document.getElementById('brissta-serving-type');
      if (!servingHeading) return false;
      const servingGroup = servingHeading.nextElementSibling;
      if (
        !servingGroup ||
        !servingGroup.classList.contains('xo-product-variant__inner-value--boxed')
      ) {
        return false;
      }
      if (servingGroup._brisstaSyncObserver) return true;
      const observer = new MutationObserver(() => {
        runAllPurchaseSyncs();
      });
      observer.observe(servingGroup, {
        attributes: true,
        attributeFilter: ['style'],
        subtree: true,
      });
      servingGroup._brisstaSyncObserver = observer;
      return true;
    };

    if (!attachServingTypeStyleObserver()) {
      // Retry until Serving Type appears
      [100, 300, 800, 1800, 3500].forEach((d) =>
        setTimeout(attachServingTypeStyleObserver, d)
      );
    }

    if (document.fonts && document.fonts.addEventListener) {
      document.fonts.addEventListener('loadingdone', runAllPurchaseSyncs);
    }
  }

  function bindWidgetEvents(block, widget) {
    // Offer card clicks (Select Offer section)
    $$('.brissta-offer-card[data-state]', widget).forEach((button) => {
      if (button._brisstaBound) return;
      button._brisstaBound = true;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        setCurrentState(block, button.dataset.state, widget);
      });
    });
    // Purchase-type radios live inside the widget for both target and
    // non-target PDPs.
    $$('.brissta-purchase-type-input', widget).forEach((input) => {
      if (input._brisstaBound) return;
      input._brisstaBound = true;
      input.addEventListener('change', () => {
        if (!input.checked) return;
        const nextType = input.dataset.purchaseType;
        let nextState;
        if (isTargetPDP()) {
          // Always reset to the first offer (offer 0 = first variant)
          // whenever Subscribe ↔ OTP is toggled.
          nextState = `${nextType}-0`;
        } else {
          nextState = buildState(nextType, defaultQuantityFor(nextType));
        }
        setCurrentState(block, nextState, widget);
      });
    });
  }

  function updateStockState() {
    const isOutOfStock = isVariantNotifyState();
    const widget = getWidget();
    const onetimeSection = $('[data-onetime-section-global]');
    const onetimeSubmit = $('[data-onetime-submit-global]');

    if (widget) widget.style.display = isOutOfStock ? 'none' : '';
    if (onetimeSection) onetimeSection.style.display = isOutOfStock ? 'none' : '';
    if (onetimeSubmit) onetimeSubmit.style.display = isOutOfStock ? 'none' : '';

    // Show/hide Klaviyo BIS container based on stock state
    const klaviyoBis = $('#klaviyo-bis-button-container');
    if (klaviyoBis) {
      klaviyoBis.style.display = isOutOfStock ? '' : 'none';
    }
    const hasKlaviyoButton = klaviyoBis && klaviyoBis.querySelector('button');

    // Show/hide unavailable section
    $$('.xo-buy-button__cart-add-unavailable').forEach((unavailDiv) => {
      if (isOutOfStock) {
        unavailDiv.style.display = 'flex';
        unavailDiv.style.flexDirection = 'column';
        unavailDiv.style.gap = '12px';
      } else {
        unavailDiv.style.display = 'none';
      }
      // Hide tooltip fallback when Klaviyo BIS is present
      const tooltip = $('xo-tooltip', unavailDiv);
      if (tooltip) {
        tooltip.classList.toggle('brissta-tooltip-hidden', !!hasKlaviyoButton);
      }
    });

    // Ensure notify copy visible when OOS
    $$('.brissta-notify-copy').forEach((copy) => {
      copy.style.display = isOutOfStock ? 'block' : 'none';
    });

    const wrapper = $('.main-product__wrapper') || $('.xo-product-info-content');
    if (wrapper) {
      wrapper.classList.toggle('brissta-oos-spacing', isOutOfStock);
    }
  }


  function restoreNotifyStateUI() {
    hideDynamicCheckoutButtons();
    hideSubscriptionAppWrappers();
    ensureNotifyCopy();
    injectServingTypeLabel();
    updateStockState();
    setPrimaryButtonVisibility(false);
  }

  function fixCartNoteButtons() {
    // Scope the observer to <xo-cart-mini> instead of document.body — both
    // targets (xo-cart-note-submit, xo-modal-trigger) live inside the cart
    // drawer, so a body-wide observer was firing on every site-wide DOM
    // mutation (xo-animate cascades, variant clicks, section refreshes…)
    // for nothing. Scoped here costs ~99% less work per interaction.
    const cartMini = document.querySelector('xo-cart-mini');
    if (!cartMini) return;
    new MutationObserver(() => {
      const submitBtn = cartMini.querySelector('xo-cart-note-submit button');
      const cancelBtn = cartMini.querySelector('xo-modal-trigger button');
      if (submitBtn && submitBtn.textContent.trim() !== 'Add') submitBtn.textContent = 'Add';
      if (cancelBtn && cancelBtn.textContent.trim() !== 'Cancel') cancelBtn.textContent = 'Cancel';
    }).observe(cartMini, { childList: true, subtree: true });
  }

  let lastVariantName = '';
  let syncFrame = null;
  let syncTimeout = null;
  let lastSyncedVariantId = '';
  let lastWidgetVariantId = '';

  function resetWidgetArtifacts() {
    const widget = getWidget();
    if (widget) widget.remove();
    const onetimeSection = $('[data-onetime-section-global]');
    if (onetimeSection) onetimeSection.remove();
    const onetimeSubmit = $('[data-onetime-submit-global]');
    if (onetimeSubmit) onetimeSubmit.remove();
    const whySub = document.getElementById('brissta-why-subscribe');
    if (whySub) whySub.remove();
    lastWidgetVariantId = '';
  }

  function syncPurchaseUI() {
    hideDynamicCheckoutButtons();
    hideSubscriptionAppWrappers();
    ensureNotifyCopy();


    const variantId = getSelectedVariantId();
    const block = getBlockForVariant();
    const outOfStock = isVariantNotifyState();

    // Out-of-stock always tears down. Missing subscription block tears
    // down on non-target PDPs, but on target PDPs we keep the offer cards
    // rendered (they drive variant selection regardless of selling-plan
    // configuration on this specific variant).
    if (outOfStock || (!hasSubscriptionBlock(block) && !isTargetPDP())) {
      resetWidgetArtifacts();
      restoreNotifyStateUI();
      lastSyncedVariantId = variantId;
      return;
    }

    if (lastWidgetVariantId !== variantId || !getWidget()) {
      resetWidgetArtifacts();
      initBlock(block);
      lastWidgetVariantId = variantId;
    } else {
      updateStockState();
      setPrimaryButtonVisibility(false);
      injectServingTypeLabel();
      const w = getWidget();
      if (w) syncPrimaryButtons(block, w);
      // Why Subscribe? panel may have been removed by an external DOM
      // update even when the variant didn't change. Re-inject if missing
      // so it stays visible for every in-stock variant with subscriptions.
      injectWhySubscribeSection();
    }

    patchAllFormValidity();

    lastSyncedVariantId = variantId;
  }

  function schedulePurchaseUISync() {
    if (syncFrame) {
      cancelAnimationFrame(syncFrame);
      syncFrame = null;
    }
    if (syncTimeout) {
      clearTimeout(syncTimeout);
      syncTimeout = null;
    }

    syncFrame = requestAnimationFrame(() => {
      syncFrame = null;
      syncPurchaseUI();

      const currentVariantId = getSelectedVariantId();
      if (currentVariantId !== lastSyncedVariantId) {
        syncTimeout = setTimeout(() => {
          syncTimeout = null;
          syncPurchaseUI();
        }, 0);
      }
    });
  }

  function onVariantChange() {
    const variantName = getSelectedVariantName();
    if (!variantName || variantName === lastVariantName) return;
    lastVariantName = variantName;
    setPrimaryButtonVisibility(true);
    resetWidgetArtifacts();
    schedulePurchaseUISync();
    // Shopify's section AJAX (triggered by xo-product on a real variant
    // change) can wipe the moved-out Purchase Type elements after our sync
    // runs. Schedule a few delayed checks to detect that and restore.
    if (isTargetPDP()) {
      [120, 350, 900].forEach(function (d) { setTimeout(restorePurchaseTypeIfMissing, d); });
    }
  }

  // Cheap, on-demand recovery: if the moved-out purchase-type elements went
  // missing (e.g., section AJAX overwrote the variants area), re-render the
  // widget to put them back. Called only at known trigger points (variant
  // change, post-init), not in any continuous loop/observer.
  function restorePurchaseTypeIfMissing() {
    if (!isTargetPDP()) return;
    if (document.querySelector('#brissta-purchase-type-label') &&
        document.querySelector('.brissta-purchase-type-variants')) return;
    const w = getWidget();
    const block = getBlockForVariant({ allowFallback: true });
    if (w) {
      refreshWidget(block, w);
    } else if (block || isTargetPDP()) {
      initBlock(block);
    }
  }

  function initBlock(block) {
    hideDynamicCheckoutButtons();
    hideSubscriptionAppWrappers();
    ensureNotifyCopy();

    if (isTargetPDP() || isMiniPackPDP()) {
      document.body.classList.add('brissta-target-offer-mode');
    }

    syncServingTypeLabels();
    injectServingTypeLabel();
    renderWidget(block);
    injectWhySubscribeSection();

    const state = normalizeState(getSavedState());
    setFormQuantity(quantityOf(state));
    setPurchaseMode(block, purchaseTypeOf(state));
    if (isTargetPDP()) {
      selectServingTypeVariant(variantIndexOfState(state));
      updateOfferImageVisibility(state);
      // Catch first-load race where section/widget render order leaves
      // the purchase-type missing. One quick check per init, no polling.
      [180, 600].forEach(function (d) { setTimeout(restorePurchaseTypeIfMissing, d); });
    }

    updateStockState();
    setPrimaryButtonVisibility(false);
  }

  function syncWhySubscribeFontSize() {
    const descSource =
      document.querySelector('.xo-product-info-content__description p span') ||
      document.querySelector('.xo-product-info-content__description p') ||
      document.querySelector('.xo-product-info-content__description');
    if (!descSource) return;
    const computed = window.getComputedStyle(descSource);
    const fontSize = computed.fontSize;
    const lineHeight = computed.lineHeight;
    const fontFamily = computed.fontFamily; // keep existing Ballinger via !important
    if (!fontSize) return;
    document
      .querySelectorAll('#brissta-why-subscribe .brissta-why-subscribe__list li')
      .forEach((el) => {
        el.style.setProperty('font-size', fontSize, 'important');
        if (lineHeight) el.style.setProperty('line-height', lineHeight, 'important');
        // intentionally don't copy font-family — Ballinger is locked via CSS
        void fontFamily;
      });
  }

  function injectWhySubscribeSection() {
    if (document.getElementById('brissta-why-subscribe')) return;
    // Anchor directly below the primary submit container so the panel sits in
    // the same column as the CTA (not a flex/grid sibling of the whole buy
    // wrapper, which ends up in the wrong cell on style-3 product layouts).
    const submitContainer =
      document.querySelector('.xo-buy-button__cart-add-add-to-card.brissta-primary-submit') ||
      document.querySelector('.xo-buy-button__cart-add-add-to-card');
    if (!submitContainer || !submitContainer.parentNode) return;

    const section = document.createElement('div');
    section.id = 'brissta-why-subscribe';
    section.className = 'brissta-why-subscribe';
    // Café Au Lait subscriptions include a free storage tin.
    var isCAL = (window.location.pathname || '').toLowerCase().indexOf('cafe-au-lait') !== -1;
    section.innerHTML =
      '<h3 class="brissta-why-subscribe__title">Why Subscribe?</h3>' +
      '<ul class="brissta-why-subscribe__list">' +
      '<li>Up to 15% off every subscription order</li>' +
      // americano-mini-pack ships flat-rate, so omit the free-shipping bullet.
      (isMiniPackPDP() ? '' : '<li>Free shipping on larger servings</li>') +
      (isCAL ? '<li>Free storage tin</li>' : '') +
      '<li>Skip, edit, or cancel anytime</li>' +
      '<li>Exclusive gifts &amp; rewards</li>' +
      '</ul>';

    if (submitContainer.nextSibling) {
      submitContainer.parentNode.insertBefore(section, submitContainer.nextSibling);
    } else {
      submitContainer.parentNode.appendChild(section);
    }

    syncWhySubscribeFontSize();
  }

  // ---------------------------------------------------------------------------
  // Shop Pay bypass — behavioural detection
  //
  // We cannot reliably detect Netskope (or any other tool causing the same
  // problem) via device globals. Instead we detect the broken behaviour:
  //
  //   1. Every checkout form submission sets a sessionStorage flag.
  //   2. If the user lands on the home page while that flag is set, checkout
  //      was interrupted mid-flow. We mark this device in localStorage, clear
  //      the stuck Shop Pay session cookies, and retry with
  //      ?skip_shopify_pay=true.
  //   3. On devices already marked as affected, we intercept checkout forms
  //      immediately and bypass Shop Pay without waiting for the first failure.
  //
  // Normal users never land on home after checkout, so the recovery path
  // never fires for them and their checkout flow is unchanged.
  // ---------------------------------------------------------------------------

  function isKnownAffectedDevice() {
    try { return localStorage.getItem('brissta_bypass_shop_pay') === '1'; } catch (e) { return false; }
  }

  function markDeviceAsAffected() {
    try { localStorage.setItem('brissta_bypass_shop_pay', '1'); } catch (e) {}
  }

  function clearShopPayCookies() {
    ['_shopify_tm', '_shopify_sa_p', '_shopify_sa_t'].forEach(function (name) {
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
    });
  }

  function bypassCheckoutRedirect() {
    clearShopPayCookies();
    sessionStorage.setItem('brissta_checkout_in_progress', '1');
    window.location.href = '/checkout?skip_shopify_pay=true';
  }

  function isCheckoutForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.id === 'xo-cart-popup-form') return true;
    if (form.querySelector('[name="checkout"]')) return true;
    return false;
  }

  function bindCheckoutIntercept() {
    document.addEventListener('submit', function (e) {
      if (!isCheckoutForm(e.target)) return;

      if (isKnownAffectedDevice()) {
        // Device already confirmed broken — skip Shop Pay immediately.
        e.preventDefault();
        e.stopImmediatePropagation();
        bypassCheckoutRedirect();
        return;
      }

      // First attempt on an unknown device — let checkout proceed normally
      // but set the flag so home recovery can detect a broken redirect.
      sessionStorage.setItem('brissta_checkout_in_progress', '1');
    }, true);
  }

  function handleHomeRecovery() {
    if (window.location.pathname !== '/') return;
    if (!sessionStorage.getItem('brissta_checkout_in_progress')) return;

    // Checkout was initiated and the user landed on home — the redirect was
    // intercepted by something (e.g. Netskope). Mark this device, clear the
    // stuck Shop Pay session cookies, and retry with the bypass parameter.
    sessionStorage.removeItem('brissta_checkout_in_progress');
    markDeviceAsAffected();
    clearShopPayCookies();
    window.location.href = '/checkout?skip_shopify_pay=true';
  }

  /* ── Brissta Sampler ── */
  function initSampler() {
    addCSS();
    patchAllFormValidity();

    // Hide the theme's variant picker and default buy button via inline styles
    // (CSS-only fails because the section's <style> in <body> overrides <head> rules)
    var samplerStyle = document.createElement('style');
    samplerStyle.textContent =
      '.brissta-sampler-ship-badge { display: none; flex: 0 0 auto; border: 1px solid #8B1D1D; border-radius: 999px; padding: 4px 10px; text-align: center; font-family: "Ballinger", sans-serif; font-size: calc(var(--brissta-card-price-size) - 4px); line-height: 1.2; opacity: 1.0; white-space: nowrap; }' +
      '.brissta-plan-card.is-active .brissta-sampler-ship-badge { display: inline-block; }' +
      '.brissta-price-section { margin: 16px 0; color: #73050A; }' +
      '.brissta-price-section__heading { font-family: BehindTheNineties, serif; font-size: clamp(20px, 1.6vw, 26px); color: #73050A; margin: 0 0 8px; line-height: 1.1; }' +
      '.brissta-price-section__row { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }' +
      '.brissta-price-section__row .brissta-plan-card__price-current { font-family: "Ballinger", sans-serif; font-size: clamp(20px, 1.5vw, 24px); color: #73050A; font-weight: 400; }' +
      '.brissta-price-section__per { font-family: BehindTheNineties, serif; font-size: clamp(20px, 1.5vw, 24px); color: #73050A; opacity: 0.85; }' +
      '.xo-product-info-content__description ul, .xo-product-info-content__description ol { padding: 0 !important; margin-left: 0 !important; list-style: none !important; }' +
      '.xo-product-info-content__description ul li, .xo-product-info-content__description ol li { position: relative !important; padding-left: 1.1em !important; list-style: none !important; }' +
      '.xo-product-info-content__description ul li::before { content: "\\2022" !important; position: absolute !important; left: 0 !important; top: 0 !important; line-height: inherit !important; font-weight: 900 !important; font-size: 1.5em !important; }' +
      '.xo-product-info-content__description ul, .xo-product-info-content__description ol, .xo-product-info-content__description ul li, .xo-product-info-content__description ol li, .xo-product-info-content__description ul li *, .xo-product-info-content__description ol li *, .xo-product-info-content__description ul li::before { font-family: "Ballinger", sans-serif !important; font-size: 12px !important; line-height: 1.45 !important; }' +
      '@media (min-width: 992px) { .xo-product-info-content__description ul, .xo-product-info-content__description ol, .xo-product-info-content__description ul li, .xo-product-info-content__description ol li, .xo-product-info-content__description ul li *, .xo-product-info-content__description ol li *, .xo-product-info-content__description ul li::before { font-size: 14px !important; line-height: 1.5 !important; } }';
    document.head.appendChild(samplerStyle);

    // Force-hide via inline style (beats any stylesheet specificity)
    $$('.xo-product-info-content__variants, .xo-product-info-content__quantity-buy-button, .xo-product-info-content__buy-button, .shopify-payment-button__group, .xo-buy-button__cart-add-add-to-card').forEach(function (el) {
      el.style.setProperty('display', 'none', 'important');
    });

    // Hide reviews section from sampler PDP
    var reviewsSection = document.getElementById('shopify-section-template--19619734224961__17742862761ff8b902');
    if (reviewsSection) reviewsSection.style.setProperty('display', 'none', 'important');

    // Swap carousel image order: CAL_Sampler second, Travel-Case last
    samplerSwapCarouselImages();

    // Inject Price section above the description
    injectSamplerPriceSection();

    // Fetch variant data from Shopify JSON endpoint
    fetch('/products/brissta-sampler.json')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var variants = data.product.variants;
        if (!variants || !variants.length) return;
        renderSamplerCards(variants);
      })
      .catch(function () {});
  }

  function injectSamplerPriceSection() {
    function tryInject() {
      if (document.getElementById('brissta-sampler-price-section')) return true;
      var desc = document.querySelector('.xo-product-info-content__description');
      if (!desc || !desc.parentNode) return false;
      var section = document.createElement('div');
      section.id = 'brissta-sampler-price-section';
      section.className = 'brissta-price-section';
      section.innerHTML =
        '<div class="brissta-price-section__row">' +
        '<span class="brissta-plan-card__price-current">$49.99</span>' +
        '<span class="brissta-price-section__per">($2.63 per coffee)</span>' +
        '</div>';
      desc.parentNode.insertBefore(section, desc);
      return true;
    }
    if (tryInject()) return;
    var attempts = 0;
    var iv = setInterval(function () {
      attempts += 1;
      if (tryInject() || attempts > 20) clearInterval(iv);
    }, 150);
  }

  function samplerSwapCarouselImages() {
    var slides = $$('xo-carousel-slide');
    var travelSlide = null;
    var calSlide = null;
    slides.forEach(function (slide) {
      var imgs = slide.querySelectorAll('img');
      imgs.forEach(function (img) {
        var src = img.getAttribute('src') || '';
        if (src.indexOf('Brissta-Travel-Case') !== -1) travelSlide = slide;
        if (src.indexOf('CAL_Sampler') !== -1) calSlide = slide;
      });
    });
    if (travelSlide && calSlide && travelSlide !== calSlide) {
      var parent = travelSlide.parentNode;
      if (!parent) return;
      // Put CAL where Travel is, then Travel at the end
      parent.insertBefore(calSlide, travelSlide);
      parent.appendChild(travelSlide);
    }
  }

  function renderSamplerCards(variants) {
    // Check URL for pre-selected variant
    var urlParams = new URLSearchParams(window.location.search);
    var preselectedVariant = urlParams.get('variant');

    // Build card markup
    var html = '<span class="brissta-purchase-title">Sampler Option</span><div class="brissta-purchase-grid">';
    variants.forEach(function (v, i) {
      var cfg = SAMPLER_CONFIG[v.title] || { perServing: '', details: [] };
      var active = preselectedVariant ? String(v.id) === preselectedVariant : i === 0;
      html += '<button type="button" class="brissta-plan-card' + (active ? ' is-active' : '') + '" data-sampler-variant="' + v.id + '" aria-pressed="' + active + '">';
      html += '<span class="brissta-plan-card__head"><span>';
      html += '<span class="brissta-plan-card__title">' + v.title + '</span>';
      if (cfg.perServing) {
        html += '<span class="brissta-plan-card__per-serving">' + cfg.perServing + '</span>';
      }
      html += '<span class="brissta-plan-card__prices"><span class="brissta-plan-card__price-current">' + samplerMoney(v.price) + '</span></span>';
      html += '</span>';
      if (cfg.badge) {
        html += '<span class="brissta-sampler-ship-badge">' + cfg.badge + '</span>';
      }
      html += '</span>';
      html += '<ul class="brissta-plan-card__details">';
      cfg.details.forEach(function (d) {
        html += '<li class="brissta-detail-item"><span class="brissta-detail-item__icon">&#10003;</span><span>' + d + '</span></li>';
      });
    });
    if (travelSlide && calSlide && travelSlide !== calSlide) {
      var parent = travelSlide.parentNode;
      if (!parent) return;
      // Put CAL where Travel is, then Travel at the end
      parent.insertBefore(calSlide, travelSlide);
      parent.appendChild(travelSlide);
    }
  }

  function renderSamplerCards(variants) {
    // Check URL for pre-selected variant
    var urlParams = new URLSearchParams(window.location.search);
    var preselectedVariant = urlParams.get('variant');

    // Resolve the active variant: URL param if valid, otherwise first variant
    var activeVariantId = null;
    if (preselectedVariant) {
      for (var vi = 0; vi < variants.length; vi++) {
        if (String(variants[vi].id) === String(preselectedVariant)) {
          activeVariantId = String(variants[vi].id);
          break;
        }
      }
    }
    if (!activeVariantId && variants[0]) activeVariantId = String(variants[0].id);

    // Sampler PDP markup: no variant cards, just the Add to cart button.
    var html = '<div class="brissta-onetime-submit"><button type="button" data-sampler-add-to-cart>Add to cart</button></div>';

    // Find injection point – insert before the (now hidden) buy button area
    var buyBtnWrap = document.querySelector('.xo-product-info-content__quantity-buy-button');
    if (!buyBtnWrap || !buyBtnWrap.parentNode) return;

    var widget = document.createElement('div');
    widget.id = 'brissta-purchase-type';
    widget.innerHTML = html;
    if (activeVariantId) widget.dataset.activeVariant = activeVariantId;
    buyBtnWrap.parentNode.insertBefore(widget, buyBtnWrap);

    function syncSamplerForm(variantId) {
      var forms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
      forms.forEach(function (form) {
        var idInput = form.querySelector('input[name="id"]');
        if (idInput) idInput.value = variantId;
        var qtyInput = form.querySelector('input[name="quantity"]');
        if (qtyInput) qtyInput.value = '1';
      });
    }

    // Initial sync – populate the product form with the active variant
    if (activeVariantId) syncSamplerForm(activeVariantId);

    // Add to cart click handler – uses same AJAX + mini cart as other PDPs
    var addBtn = widget.querySelector('[data-sampler-add-to-cart]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var variantId = widget.dataset.activeVariant;
        if (!variantId || addToCartInFlight) return;

        addToCartInFlight = true;
        var form = getProductForm();
        if (!form) { addToCartInFlight = false; return; }

        // Set correct variant and quantity directly
        var idInput = $('input[name="id"]', form);
        if (idInput) idInput.value = variantId;
        var qtyInput = $('input[name="quantity"]', form);
        if (qtyInput) qtyInput.value = '1';
        // Clear any selling plan
        var planInput = $('input[name="selling_plan"]', form);
        if (planInput) planInput.value = '';

        var formData = new FormData(form);
        formData.set('sections', getServiceSections().join(','));
        formData.set('section_url', window.location.pathname);

        fetch((window.Shopify.routes.root || '/') + 'cart/add', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }
        })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          applyCartSections(data.sections);
          if (window.xoModal && window.xoModal.open) {
            window.xoModal.open('cart');
          }
        })
        .catch(function () {})
        .finally(function () { addToCartInFlight = false; });
      });
    }
  }

  function boot() {
    // Sampler product: render sampler cards instead of subscription UI
    if (window.location.pathname === '/products/brissta-sampler') {
      initSampler();
      return;
    }

    hardReloadInFlight = false;
    addCSS();
    fixCartNoteButtons();
    syncServingTypeLabels();
    lastVariantName = getSelectedVariantName();
    bindCheckoutIntercept();
    handleHomeRecovery();

    // Everything below drives product forms (purchase UI, variant labels,
    // form patching and a subtree MutationObserver). Pages without one —
    // homepage, content pages, cart — stop here so the observer doesn't run
    // on every DOM change site-wide.
    if (!$('xo-product') && !$('.main-product__wrapper')) return;

    schedulePurchaseUISync();

    // Patch checkValidity on ALL product forms so the Shopify
    // Subscriptions app's hidden required fields (and any other app's
    // required fields) don't block the theme's AJAX add-to-cart handler.
    // The forms already have novalidate, so browser validation is not
    // expected. Without this, the theme's handleAddToCart bails before
    // calling preventDefault(), causing native form submission to /cart.
    patchAllFormValidity();

    // Prevent native form submission on product forms. The theme's
    // handleAddToCart (click handler) does AJAX add + opens mini cart.
    // If anything causes a native submit (subscription app re-render,
    // unbound handler, etc.), block it to avoid /cart redirect.
    // We ONLY prevent default — never trigger an add-to-cart here,
    // because submit events can fire from the subscription app's
    // internal re-renders, not just user clicks.
    document.addEventListener('submit', function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[action*="/cart/add"]')) return;
      if (form !== getProductForm()) return;
      if (!event.defaultPrevented) {
        event.preventDefault();
      }
    }, true);

    document.addEventListener('change', function (event) {
      if (event.target.classList.contains('xo-field-radio-3__input')) {
        onVariantChange();
      }
    });

    const stockObserver = new MutationObserver(() => {
      syncServingTypeLabels();
      schedulePurchaseUISync();
    });
    // Observe both the wrapper and xo-product element for attribute changes
    const observerTargets = [
      $('.main-product__wrapper'),
      $('xo-product'),
      document.body
    ].filter(Boolean);
    const primaryTarget = observerTargets[0];
    stockObserver.observe(primaryTarget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['xo-unavailable', 'xo-disabled', 'checked', 'value']
    });
    // If xo-product is separate from wrapper, observe it too
    const xoProduct = $('xo-product');
    if (xoProduct && xoProduct !== primaryTarget && !primaryTarget.contains(xoProduct)) {
      stockObserver.observe(xoProduct, {
        attributes: true,
        attributeFilter: ['xo-unavailable', 'xo-disabled']
      });
    }

  }

  // Force full page reload when navigating to product pages from other pages.
  // The site uses SPA navigation (PageSpeed) which prevents Klaviyo's BIS
  // script from re-initializing. Product pages need a full reload so
  // Klaviyo can detect out-of-stock variants and render the BIS button.
  document.addEventListener('click', function (e) {
    const link = e.target.closest('a[href]');
    if (!link) return;
    var href = link.getAttribute('href') || '';
    if (!/\/products\/[^?#]+/.test(href)) return;
    var resolved = new URL(href, window.location.origin);
    if (resolved.pathname === window.location.pathname) return;
    e.preventDefault();
    e.stopPropagation();
    window.location.href = resolved.href;
  }, true);

  // When user clicks an OOS variant radio and Klaviyo hasn't rendered its BIS
  // button, reload the page with that variant selected so Klaviyo initializes.
  // Uses click (not change) in capture phase — fires before anything else.
  // stopImmediatePropagation prevents theme from processing the click.
  document.addEventListener('click', function (e) {
    var label = e.target.closest('label.xo-field-radio-3');
    if (!label) return;
    var radio = label.querySelector('input.xo-field-radio-3__input');
    if (!radio || !radio.hasAttribute('xo-disabled')) return;
    var variantId = radio.getAttribute('xo-variant-id');
    if (!variantId) return;
    // Skip if Klaviyo already rendered its BIS button
    var bis = document.querySelector('#klaviyo-bis-button-container');
    if (bis && bis.querySelector('button')) return;
    // Block all other handlers and reload with OOS variant
    e.preventDefault();
    e.stopImmediatePropagation();
    window.location.href = window.location.pathname + '?variant=' + variantId;
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bindRouteChangeReload();
      boot();
    });
  } else {
    bindRouteChangeReload();
    boot();
  }
})();
